import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient } from "@/lib/api-client.js";
import { num, eur, pct } from "@/lib/format.js";
import { RoofLayoutSvg } from "@/components/RoofLayoutSvg.js";
import { RoofPolygonLayoutSvg } from "@/components/RoofPolygonLayoutSvg.js";
import { computeBelegung } from "@/lib/roof-belegung.js";
import { summarizeManualLayout, toggleDisabledModule } from "@/lib/module-edit.js";
import {
  gridLayoutPlanar,
  rectFromArea,
  polygonsToDxf,
  roofDesignToGeoJSON,
  latLngPolysToLocalMeters,
  type DxfPolyline,
  COPILOT_DISCLAIMER,
  type RoofCopilotAdvice,
} from "@energyos/roof-core";
import { normalizeRoofSemanticData } from "@energyos/shared-types";
import { ProjectCopilot } from "@/components/ProjectCopilot.js";
import { PresentationMode } from "./PresentationMode.js";
import { PlanningCopilot } from "@/components/PlanningCopilot.js";
import { AddressAutocomplete } from "@/components/AddressAutocomplete.js";
import { usePvgis } from "@/hooks/usePvgis.js";
import type { PvgisResult } from "@/hooks/usePvgis.js";
import { StatusBadge } from "@/components/StatusBadge.js";
import { RoofDrawMap } from "@/components/RoofDrawMap.js";
import type { LatLng } from "@/components/RoofDrawMap.js";
import { RoofEditor } from "@/components/RoofEditor.js";
import type { RoofSegment } from "@/components/RoofEditor.js";
import { DachformSelector } from "@/components/DachformSelector.js";
import type { Dachform } from "@/components/DachformSelector.js";
import { KonzeptPanel } from "@/modules/konzept/KonzeptPanel.js";
import { VariantenPanel } from "@/modules/konzept/VariantenPanel.js";
import { CostWorksheetPanel } from "@/modules/costing/CostWorksheetPanel.js";
import { StuecklistePrintSheet } from "@/modules/components/StuecklistePrintSheet.js";
import { BomPrintSheet } from "@/modules/planning/BomPrintSheet.js";
import { bomToCsv, bomCsvFilename } from "@/modules/planning/bom-export.js";
import type { BillOfMaterials } from "@/modules/planning/bom-export.js";
import { RoofDesignerTab } from "@/modules/roofdesigner/RoofDesignerTab.js";
import { EmptyState } from "@/components/ui/EmptyState.js";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary.js";
import { StorageB5WorksheetPanel } from "@/modules/konzept/StorageB5WorksheetPanel.js";
import { ReviewPanel } from "@/modules/review/ReviewPanel.js";
import { StructuralTab } from "@/modules/projects/StructuralTab.js";
import { ShadingTab } from "@/modules/projects/ShadingTab.js";
import { PvAnalyseTab } from "@/modules/projects/PvAnalyseTab.js";
// Code-split: three.js (~600 KB) wird erst geladen, wenn der 3D-Schatten-Tab geöffnet wird.
const Shading3DTab = lazy(() =>
  import("@/modules/projects/Shading3DTab.js").then((m) => ({ default: m.Shading3DTab })),
);
import { PlanerTab } from "@/modules/projects/planer/PlanerTab.js";
import { useAuthStore } from "@/stores/auth.store.js";

const TABS = [
  { key: "planer",        label: "Planer" },
  { key: "overview",      label: "Übersicht" },
  { key: "geospatial",    label: "Gebäude" },
  { key: "roofdesigner",  label: "Dachplaner" },
  { key: "planning",      label: "Planung" },
  { key: "simulation",    label: "Simulation" },
  { key: "economics",     label: "Wirtschaftlichkeit" },
  { key: "engineering",   label: "Elektroplanung" },
  { key: "structural",    label: "Statik" },
  { key: "shading",       label: "Verschattung" },
  { key: "shading3d",     label: "3D-Schatten" },
  { key: "pvanalyse",     label: "PV-Analyse" },
  { key: "costing",       label: "Kalkulation" },
  { key: "komponenten",   label: "Komponenten" },
  { key: "presentation",  label: "Steckbrief" },
  { key: "konzept",       label: "Konzept" },
  { key: "ki-pruefung",   label: "KI-Prüfung" },
  { key: "review",        label: "Prüfung" },
  { key: "approvals",     label: "Freigaben" },
  { key: "servicehub",    label: "ServiceHub" },
  { key: "audit",         label: "Audit" },
];

// Gruppierte 2-Ebenen-Navigation: 6 Bereiche statt 14 flacher Tabs.
// "overview" ist ein eigener Bereich ohne Untertabs.
// Kontextuelle Copilot-Hinweise (statisch, deterministisch) — Reuse der PlanningCopilot-UI.
// Strukturell canApprove=false / requiresHumanReview=true; Disclaimer immer sichtbar.
const KONZEPT_COPILOT_ADVICE: RoofCopilotAdvice = {
  headline: "Konzept ist ein ENTWURF — Daten prüfen, Freigabe nur durch Menschen.",
  readiness: "caution",
  hints: [
    { code: "entwurf", severity: "info", title: "Bleibt ENTWURF", detail: "Die KI gibt das Konzept nicht frei; Status bleibt Entwurf bis zur menschlichen Prüfung.", nextStep: "Inhalt fachlich prüfen, dann manuell freigeben." },
    { code: "daten", severity: "warning", title: "Fehlende Daten kennzeichnen", detail: "Unvollständige Eingaben werden als Annahme bzw. zu prüfen markiert — nie als Fakt.", nextStep: "Verbrauch/Ertrag/Variante vervollständigen." },
  ],
  nextStep: "Konzept-Inhalt prüfen und fehlende Daten ergänzen.",
  disclaimer: COPILOT_DISCLAIMER,
  requiresHumanReview: true,
  canApprove: false,
};
const STRINGING_COPILOT_ADVICE: RoofCopilotAdvice = {
  headline: "Strang-/WR-Auslegung & Kalkulation sind Entwurf — prüfen, nicht zusichern.",
  readiness: "caution",
  hints: [
    { code: "string", severity: "warning", title: "Stringing/MPPT prüfen", detail: "Strang-/MPPT-/WR-Fenster muss eingehalten sein (Kälte-Voc/Heiß-Vmp) — sonst Sicherheits-/Haftungsrisiko.", nextStep: "Strang-Check mit Modul-/WR-Datenblatt ausführen." },
    { code: "pvgis", severity: "warning", title: "PVGIS-Referenz", detail: "Fehlt der standortbezogene Ertrag, sind Stringing-Annahmen und Wirtschaftlichkeit nicht belastbar.", nextStep: "PVGIS-/Ertragssimulation ausführen." },
    { code: "kosten", severity: "info", title: "Kosten = Richtwert", detail: "Kalkulationswerte sind unverbindliche Richtwerte/Entwurf bis zur Vor-Ort-Prüfung.", nextStep: "Angebot erst nach Vor-Ort-Besichtigung verbindlich machen." },
  ],
  nextStep: "Strang-Check ausführen und PVGIS-Ertrag sicherstellen.",
  disclaimer: COPILOT_DISCLAIMER,
  requiresHumanReview: true,
  canApprove: false,
};

const TAB_GROUPS: { key: string; label: string; tabs: string[] }[] = [
  { key: "planer",     label: "Planer",     tabs: ["planer"] },
  { key: "overview",   label: "Übersicht",  tabs: ["overview"] },
  { key: "planung",    label: "Planung",    tabs: ["geospatial", "roofdesigner", "planning", "simulation", "engineering", "structural", "shading", "shading3d", "pvanalyse"] },
  { key: "wirtschaft", label: "Wirtschaft", tabs: ["economics", "costing", "komponenten"] },
  { key: "dokumente",  label: "Dokumente",  tabs: ["presentation", "konzept"] },
  { key: "freigabe",   label: "Freigabe",   tabs: ["ki-pruefung", "review", "approvals", "servicehub"] },
  { key: "system",     label: "System",     tabs: ["audit"] },
];

function groupForTab(tabKey: string): string {
  return TAB_GROUPS.find((g) => g.tabs.includes(tabKey))?.key ?? "overview";
}

function tabLabel(tabKey: string): string {
  return TABS.find((t) => t.key === tabKey)?.label ?? tabKey;
}

interface Project {
  id: string;
  name: string;
  status: string;
  address: string;
  buildingType: string;
  notes: string | null;
  customer: { id: string; name: string };
  revisions: Array<{ id: string; revisionNumber: number; label: string; isLocked: boolean; createdAt: string }>;
  approvals: Array<{ id: string; type: string; status: string; revisionId: string }>;
  risk?: "red" | "yellow" | "green" | "grey";
  confidencePct?: number | null;
  updatedAt: string;
}

// §5/§7 Risikoampel im Detail-Kopf (Farbe + Symbol + Text, §22-konform).
const HEADER_RISK: Record<string, { color: string; symbol: string; label: string }> = {
  red: { color: "#dc2626", symbol: "●", label: "Kritisch" },
  yellow: { color: "#d97706", symbol: "▲", label: "Prüfen" },
  green: { color: "#16a34a", symbol: "✓", label: "OK" },
  grey: { color: "#94a3b8", symbol: "○", label: "Keine Daten" },
};

const APPROVAL_TYPE_LABELS: Record<string, string> = {
  roof_model: "Dachmodell",
  shading: "Verschattungsanalyse",
  structural_precheck: "Statische Vorbewertung",
  electrical_planning: "Elektrische Planung",
  economic_analysis: "Wirtschaftlichkeitsanalyse",
  material_list: "Materialliste",
  offer_export: "Angebotsexport",
};
const APPROVAL_TYPES = Object.keys(APPROVAL_TYPE_LABELS);
// Spiegelt APPROVAL_ROLE_MAP im Backend (approvals.service.ts) — wer welche
// Freigabe erteilen darf. Wird im UI genutzt, um Buttons rollenabhängig zu
// sperren (statt den Nutzer in einen stillen 403 laufen zu lassen).
const APPROVAL_ALLOWED_ROLES: Record<string, string[]> = {
  roof_model: ["planner", "management", "admin"],
  shading: ["planner", "management", "admin"],
  structural_precheck: ["planner", "management", "admin"],
  electrical_planning: ["planner", "management", "admin"],
  economic_analysis: ["planner", "management", "admin"],
  material_list: ["planner", "management", "admin"],
  offer_export: ["management", "admin"],
};

const MONTH_NAMES = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showPresentation, setShowPresentation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locking, setLocking] = useState(false);

  const reloadProject = async () => {
    if (!id) return;
    const res = await apiClient.get<Project>(`/projects/${id}`);
    if (res.success) setProject(res.data);
  };

  useEffect(() => {
    if (!id) return;
    void (async () => {
      const res = await apiClient.get<Project>(`/projects/${id}`);
      if (res.success) setProject(res.data);
      setLoading(false);
    })();
  }, [id]);

  const handleStatusChanged = (newStatus: string) => {
    setProject(p => p ? { ...p, status: newStatus } : p);
  };

  // Revision sperren — Voraussetzung für den ServiceHub-Export (guard2). Vorher
  // gab es KEIN UI dafür → der Export war dauerhaft unerreichbar.
  const handleLockRevision = async () => {
    const rev = project?.revisions[0];
    if (!project || !rev || rev.isLocked) return;
    setLocking(true);
    await apiClient.post(`/projects/${project.id}/revisions/${rev.id}/lock`, {});
    await reloadProject();
    setLocking(false);
  };

  if (loading) return <div style={{ padding: 48, textAlign: "center", color: "var(--color-text-muted)" }}>Lade Projekt…</div>;
  if (!project) {
    return (
      <div style={{ padding: 48 }}>
        <EmptyState
          title="Projekt nicht gefunden"
          description="Das Projekt existiert nicht oder gehört zu einem anderen Mandanten."
          action={<Link to="/projects" className="btn-secondary" style={{ fontSize: 13 }}>Zur Projektliste</Link>}
        />
      </div>
    );
  }

  const currentRevision = project.revisions[0];

  return (
    <div>
      {/* §20 Präsentationsmodus (Vollbild-Overlay über allem) */}
      {showPresentation && (
        <PresentationMode
          projectId={project.id}
          projectName={project.name}
          customerName={project.customer.name}
          address={project.address}
          onClose={() => setShowPresentation(false)}
        />
      )}

      {/* Breadcrumb */}
      <div style={{ fontSize: 13, color: "var(--color-text-muted)", marginBottom: 8 }}>
        <Link to="/projects" style={{ color: "var(--color-text-muted)" }}>Projekte</Link> → {project.name}
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{project.name}</h1>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
            <StatusBadge status={project.status} />
            {(() => { const r = HEADER_RISK[project.risk ?? "grey"] ?? HEADER_RISK.grey!; return (
              <span title="Risikoampel (aus den Prüfergebnissen)" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: r.color, fontWeight: 600 }}>
                <span aria-hidden="true">{r.symbol}</span>{r.label}
              </span>
            ); })()}
            {project.confidencePct != null && (
              <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>Vertrauen: <strong>{project.confidencePct} %</strong></span>
            )}
            <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>{project.customer.name}</span>
            <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>·</span>
            <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>{project.address}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {/* §20: geführter 7-Seiten-Kundenmodus (Vollbild). */}
          <button
            className="btn-primary"
            style={{ fontSize: 13 }}
            data-testid="start-presentation-mode"
            onClick={() => setShowPresentation(true)}
          >
            ▶ Präsentationsmodus
          </button>
          {/* Öffnet den bestehenden Steckbrief-Tab (Tab-Key "presentation"). */}
          <button
            className="btn-secondary"
            style={{ fontSize: 13 }}
            data-testid="open-presentation-tab"
            onClick={() => setActiveTab("presentation")}
          >
            Steckbrief
          </button>
        </div>
      </div>

      {/* Revision indicator */}
      {currentRevision && (
        <div style={{ background: "#f0f4ff", border: "1px solid #c7d2fe", borderRadius: 6, padding: "8px 14px", marginBottom: 16, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>
            Aktuelle Revision: <strong>{currentRevision.label}</strong> (Rev. {currentRevision.revisionNumber})
            {currentRevision.isLocked && <span style={{ marginLeft: 8, color: "var(--color-warning)", fontWeight: 600 }}>🔒 Gesperrt</span>}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {!currentRevision.isLocked && (
              <button
                className="btn-secondary"
                style={{ fontSize: 12, padding: "4px 10px" }}
                onClick={() => void handleLockRevision()}
                disabled={locking}
                title="Revision für den Angebots-/ServiceHub-Export sperren"
              >
                {locking ? "Sperre…" : "🔒 Revision sperren"}
              </button>
            )}
            <span style={{ color: "var(--color-text-muted)" }}>{new Date(currentRevision.createdAt).toLocaleDateString("de-AT")}</span>
          </span>
        </div>
      )}

      {/* KI-Copilot: projektweite Planungs-Readiness (deterministisch, ENTWURF) */}
      {id && (
        <div style={{ marginBottom: 16 }}>
          <ProjectCopilot projectId={id} />
        </div>
      )}

      {/* Gruppierte Navigation — Ebene 1: Bereiche */}
      {(() => {
        const activeGroup = groupForTab(activeTab);
        const group = TAB_GROUPS.find((g) => g.key === activeGroup);
        const subTabs = group && group.tabs.length > 1 ? group.tabs : [];
        return (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", gap: 2, borderBottom: "2px solid var(--color-border)" }}>
              {TAB_GROUPS.map((g) => (
                <button
                  key={g.key}
                  onClick={() => setActiveTab(g.tabs[0] ?? "overview")}
                  style={{
                    background: "none",
                    border: "none",
                    borderBottom: activeGroup === g.key ? "2px solid var(--color-primary)" : "2px solid transparent",
                    borderRadius: 0,
                    color: activeGroup === g.key ? "var(--color-primary)" : "var(--color-text-muted)",
                    fontWeight: activeGroup === g.key ? 600 : 400,
                    padding: "9px 16px",
                    fontSize: 14,
                    whiteSpace: "nowrap",
                    marginBottom: -2,
                    cursor: "pointer",
                  }}
                >
                  {g.label}
                </button>
              ))}
            </div>
            {/* Ebene 2: Untertabs der aktiven Gruppe (nur wenn mehr als einer) */}
            {subTabs.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingTop: 12 }}>
                {subTabs.map((tk) => (
                  <button
                    key={tk}
                    onClick={() => setActiveTab(tk)}
                    style={{
                      fontSize: 12,
                      border: `1px solid ${activeTab === tk ? "var(--color-primary)" : "var(--color-border)"}`,
                      background: activeTab === tk ? "var(--color-primary)" : "transparent",
                      color: activeTab === tk ? "#fff" : "var(--color-text-muted)",
                      padding: "6px 14px",
                      borderRadius: 999,
                      cursor: "pointer",
                      fontWeight: activeTab === tk ? 600 : 400,
                    }}
                  >
                    {tabLabel(tk)}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Tab Content — eigene Fehlergrenze je Tab: ein Render-Fehler in EINEM
          Tab darf NICHT die ganze App (Header + Navigation) weißen. key={activeTab}
          setzt die Boundary beim Tab-Wechsel zurück. */}
      <ErrorBoundary
        key={activeTab}
        fallback={
          <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--color-text-muted)" }}>
            Dieser Bereich konnte nicht geladen werden. Bitte einen anderen Tab wählen oder die Seite neu laden.
          </div>
        }
      >
        <TabContent tab={activeTab} project={project} onStatusChanged={handleStatusChanged} />
      </ErrorBoundary>
    </div>
  );
}

function TabContent({ tab, project, onStatusChanged }: { tab: string; project: Project; onStatusChanged: (s: string) => void }) {
  const revisionId = project.revisions[0]?.id ?? "";
  switch (tab) {
    case "planer":
      return (
        <PlanerTab
          project={project}
          renderTab={(k) => <TabContent tab={k} project={project} onStatusChanged={onStatusChanged} />}
        />
      );
    case "overview":
      return <OverviewTab project={project} onStatusChanged={onStatusChanged} />;
    case "geospatial":
      return <BuildingTab projectId={project.id} />;
    case "roofdesigner":
      return <RoofDesignerTab projectId={project.id} />;
    case "planning":
      return <PlanningTab projectId={project.id} revisionId={revisionId} />;
    case "simulation":
      return <SimulationTab projectId={project.id} revisionId={revisionId} />;
    case "economics":
      return <EconomicsTab projectId={project.id} revisionId={revisionId} />;
    case "engineering":
      return <EngineeringTab projectId={project.id} revisionId={revisionId} />;
    case "structural":
      return <StructuralTab projectId={project.id} revisionId={revisionId} />;
    case "shading":
      return <ShadingTab projectId={project.id} />;
    case "shading3d":
      return (
        <Suspense
          fallback={<div style={{ padding: 32, color: "var(--color-text-muted)" }}>Lade 3D-Ansicht…</div>}
        >
          <Shading3DTab projectId={project.id} />
        </Suspense>
      );
    case "pvanalyse":
      return (
        <>
          <div style={{ marginBottom: 14 }}><PlanningCopilot advice={STRINGING_COPILOT_ADVICE} title="KI-Copilot · Auslegung" /></div>
          <PvAnalyseTab projectId={project.id} />
        </>
      );
    case "costing":
      return (
        <>
          <div style={{ marginBottom: 14 }}><PlanningCopilot advice={STRINGING_COPILOT_ADVICE} title="KI-Copilot · Kalkulation" /></div>
          <CostWorksheetPanel projectId={project.id} />
        </>
      );
    case "komponenten":
      return <KomponentenTab projectId={project.id} />;
    case "presentation":
      return <SteckbriefTab projectId={project.id} />;
    case "konzept":
      return (
        <>
          <div style={{ marginBottom: 14 }}><PlanningCopilot advice={KONZEPT_COPILOT_ADVICE} title="KI-Copilot · Konzept" /></div>
          <VariantenPanel projectId={project.id} />
          <KonzeptPanel projectId={project.id} projectAddress={project.address} />
        </>
      );
    case "ki-pruefung":
      return <KiPruefungTab projectId={project.id} />;
    case "review":
      return <ReviewPanel projectId={project.id} />;
    case "approvals":
      return <ApprovalsTab projectId={project.id} revisionId={revisionId} approvals={project.approvals} />;
    case "servicehub":
      return <ServiceHubTab project={project} />;
    case "audit":
      return <AuditTab projectId={project.id} />;
    default:
      return <PlaceholderTab name={TABS.find((t) => t.key === tab)?.label ?? tab} />;
  }
}

// ─── Workflow constants ────────────────────────────────────────────────────────

const STATUS_FLOW = [
  "draft", "geo_analysis", "planning", "simulation",
  "technical_review", "economic_review", "ready_for_offer",
  "sent_to_servicehub", "offered", "accepted",
  "in_execution", "commissioned", "monitoring", "archived",
] as const;

const STATUS_LABELS: Record<string, string> = {
  draft: "Entwurf",
  geo_analysis: "Geodaten",
  planning: "Planung",
  simulation: "Simulation",
  technical_review: "Tech.Prüfung",
  economic_review: "Wirtschaft.",
  ready_for_offer: "Angebotsbereit",
  sent_to_servicehub: "ServiceHub",
  offered: "Angeboten",
  accepted: "Angenommen",
  in_execution: "Ausführung",
  commissioned: "Inbetrieb",
  monitoring: "Monitoring",
  archived: "Archiviert",
};

// Forward transitions only (first allowed target per status)
const NEXT_STATUS: Record<string, string> = {
  draft: "geo_analysis",
  geo_analysis: "planning",
  planning: "simulation",
  simulation: "technical_review",
  technical_review: "economic_review",
  economic_review: "ready_for_offer",
  ready_for_offer: "sent_to_servicehub",
  sent_to_servicehub: "offered",
  offered: "accepted",
  accepted: "in_execution",
  in_execution: "commissioned",
  commissioned: "monitoring",
  monitoring: "archived",
};

// ─── WorkflowWidget ───────────────────────────────────────────────────────────

function WorkflowWidget({ project, onStatusChanged }: { project: Project; onStatusChanged: (newStatus: string) => void }) {
  const [advancing, setAdvancing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentIdx = STATUS_FLOW.indexOf(project.status as typeof STATUS_FLOW[number]);
  const nextStatus = NEXT_STATUS[project.status];

  const handleAdvance = async () => {
    if (!nextStatus) return;
    setAdvancing(true);
    setError(null);
    const res = await apiClient.post<{ success: boolean }>(
      `/workflow/transition/${project.id}`,
      { targetStatus: nextStatus },
    );
    if (res.success) {
      onStatusChanged(nextStatus);
    } else {
      setError("Status-Wechsel nicht möglich. Prüfen Sie die Voraussetzungen.");
    }
    setAdvancing(false);
  };

  // Show a condensed 7-step view: first 4 + last 3, with dots in between when needed
  const visibleStatuses = STATUS_FLOW.slice(0, 7);

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Workflow-Status</h3>
      {/* Progress-Bar */}
      <div style={{ display: "flex", gap: 3, marginBottom: 8 }}>
        {STATUS_FLOW.map((s, i) => (
          <div
            key={s}
            style={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              background: i <= currentIdx ? "var(--color-primary)" : "var(--color-border)",
              transition: "background 0.3s",
            }}
          />
        ))}
      </div>
      {/* Labels — show condensed set */}
      <div style={{ display: "flex", gap: 3, marginBottom: 12 }}>
        {visibleStatuses.map((s) => (
          <div
            key={s}
            style={{
              flex: 1,
              fontSize: 10,
              textAlign: "center",
              color: s === project.status ? "var(--color-primary)" : "var(--color-text-muted)",
              fontWeight: s === project.status ? 700 : 400,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {STATUS_LABELS[s]}
          </div>
        ))}
        {STATUS_FLOW.length > visibleStatuses.length && (
          <div style={{ flex: 1, fontSize: 10, textAlign: "center", color: "var(--color-text-muted)" }}>…</div>
        )}
      </div>
      <div style={{ fontSize: 13, marginBottom: 10 }}>
        Aktuell:{" "}
        <strong style={{ color: "var(--color-primary)" }}>
          {STATUS_LABELS[project.status] ?? project.status}
        </strong>
      </div>
      {error && (
        <div style={{ fontSize: 12, color: "var(--color-error)", marginBottom: 8 }}>{error}</div>
      )}
      {nextStatus && project.status !== "archived" ? (
        <button
          className="btn-primary"
          onClick={() => void handleAdvance()}
          disabled={advancing}
          style={{ fontSize: 13 }}
        >
          {advancing ? "Wechsle…" : `Weiter: ${STATUS_LABELS[nextStatus] ?? nextStatus}`}
        </button>
      ) : (
        <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>Projekt archiviert.</span>
      )}
    </div>
  );
}

// ─── NotesWidget ──────────────────────────────────────────────────────────────

function NotesWidget({ projectId, initialNotes }: { projectId: string; initialNotes: string | null }) {
  const [notes, setNotes] = useState(initialNotes ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    await apiClient.patch(`/projects/${projectId}/notes`, { notes });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600 }}>Projektnotizen</h3>
        <button
          className="btn-secondary"
          onClick={() => void handleSave()}
          disabled={saving}
          style={{ fontSize: 12 }}
        >
          {saving ? "Speichere…" : saved ? "Gespeichert" : "Speichern"}
        </button>
      </div>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Interne Notizen zum Projekt…"
        rows={4}
        style={{
          width: "100%",
          padding: "8px 10px",
          border: "1px solid var(--color-border)",
          borderRadius: 6,
          fontSize: 13,
          resize: "vertical",
          fontFamily: "inherit",
          background: "var(--color-bg)",
          color: "var(--color-text)",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────

// KPI-Kennzahlen der Übersicht — echte Werte aus den jeweiligen Modulen.
function ProjectKpiRow({ projectId }: { projectId: string }) {
  const [kwp, setKwp] = useState<number | null>(null);
  const [yieldKwh, setYieldKwh] = useState<number | null>(null);
  const [investEur, setInvestEur] = useState<number | null>(null);
  const [paybackY, setPaybackY] = useState<number | null>(null);

  useEffect(() => {
    // Anlagengröße — Priorität: 1) In-Projekt-Modulbelegung (Planung-Tab,
    // PVModulePlacement, Summe über alle Dachflächen), 2) Dachentwurf
    // (roofdesigner), 3) Wirtschaftlichkeitsanalyse. Die In-Projekt-Belegung
    // ist die direkteste „so wurde geplant"-Zahl und gewinnt daher.
    void apiClient
      .get<Array<{ layoutData?: { systemSizeKwp?: number } }>>(`/projects/${projectId}/planning`)
      .then((r) => {
        if (r.success && Array.isArray(r.data) && r.data.length > 0) {
          const sum = r.data.reduce(
            (acc, p) => acc + (typeof p.layoutData?.systemSizeKwp === "number" ? p.layoutData.systemSizeKwp : 0),
            0,
          );
          if (sum > 0) setKwp(sum);
        }
      });
    void apiClient
      .get<Array<{ summaryJson?: { result?: { estimatedKwp?: number } } }>>(`/projects/${projectId}/roof-designs`)
      .then((r) => {
        if (r.success && Array.isArray(r.data) && r.data.length > 0) {
          const k = r.data[0]?.summaryJson?.result?.estimatedKwp;
          if (typeof k === "number" && k > 0) setKwp((prev) => prev ?? k);
        }
      });
    // Jahresertrag: neueste abgeschlossene Simulation. Einzelfläche liefert
    // annualYieldKwh, Mehrflächen-Sim totalAnnualYieldKwh — beide akzeptieren.
    void apiClient
      .get<Array<{ status?: string; mode?: string; result?: { annualYieldKwh?: number; totalAnnualYieldKwh?: number } }>>(`/projects/${projectId}/simulation`)
      .then((r) => {
        if (!r.success || !Array.isArray(r.data)) return;
        const run = r.data.find(
          (s) =>
            s.status === "completed" &&
            s.mode !== "shading" &&
            (typeof s.result?.annualYieldKwh === "number" || typeof s.result?.totalAnnualYieldKwh === "number"),
        );
        const y = run?.result?.annualYieldKwh ?? run?.result?.totalAnnualYieldKwh;
        if (typeof y === "number" && y > 0) setYieldKwh(y);
      });
    // Investition + Amortisation: neueste Wirtschaftlichkeitsanalyse.
    void apiClient
      .get<Array<{ systemSizeKwp?: number; investmentEur?: number; result?: { paybackYears?: number } }>>(`/projects/${projectId}/economics`)
      .then((r) => {
        if (!r.success || !Array.isArray(r.data) || r.data.length === 0) return;
        const a = r.data[0];
        if (typeof a?.investmentEur === "number") setInvestEur(a.investmentEur);
        if (typeof a?.systemSizeKwp === "number" && a.systemSizeKwp > 0) setKwp((prev) => prev ?? a.systemSizeKwp!);
        const pb = a?.result?.paybackYears;
        if (typeof pb === "number" && pb > 0) setPaybackY(pb);
      });
    // Fallback Investition: berechnete Kalkulation (Card-Hinweis sagt „nach
    // Kalkulation") — bisher verwaist. Nur setzen, wenn keine Economics-Analyse.
    void apiClient
      .get<{ items: Array<{ totalNetEur?: number }> }>(`/projects/${projectId}/costing/calculations`)
      .then((r) => {
        if (!r.success) return;
        const net = r.data?.items?.[0]?.totalNetEur;
        if (typeof net === "number" && net > 0) setInvestEur((prev) => prev ?? net);
      });
  }, [projectId]);

  const fmt = (v: number | null, unit: string, hint: string, digits = 0) => ({
    value: v != null ? `${v.toLocaleString("de-AT", { maximumFractionDigits: digits })} ${unit}` : "—",
    hint: v != null ? null : hint,
  });

  const cards = [
    { label: "Anlage", ...fmt(kwp, "kWp", "nach Dachplanung", 2) },
    { label: "Jahresertrag", ...fmt(yieldKwh, "kWh", "nach Simulation") },
    { label: "Investition", ...fmt(investEur, "€", "nach Kalkulation") },
    { label: "Amortisation", ...fmt(paybackY, "Jahre", "nach Wirtschaftlichkeit", 1) },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
      {cards.map((c) => (
        <div key={c.label} style={{ background: "var(--color-bg-subtle, #f8fafc)", borderRadius: 8, padding: "14px 16px" }}>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{c.label}</div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 2 }}>{c.value}</div>
          {c.hint && <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{c.hint}</div>}
        </div>
      ))}
    </div>
  );
}

function OverviewTab({ project, onStatusChanged }: { project: Project; onStatusChanged: (s: string) => void }) {
  return (
    <div>
      {/* KPI-Kennzahlen (echte Werte, sobald vorhanden) */}
      <ProjectKpiRow projectId={project.id} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Nächster Schritt + Notizen */}
        <div>
          <WorkflowWidget project={project} onStatusChanged={onStatusChanged} />
          <NotesWidget projectId={project.id} initialNotes={project.notes} />
        </div>
        {/* Projektdaten */}
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Projektdaten</h3>
          {[
            ["Name", project.name],
            ["Adresse", project.address],
            ["Gebäudetyp", project.buildingType],
            ["Kunde", project.customer.name],
            ["Status", STATUS_LABELS[project.status] ?? project.status],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--color-border)" }}>
              <span style={{ color: "var(--color-text-muted)", fontSize: 13 }}>{k}</span>
              <span style={{ fontWeight: 500, fontSize: 13 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Google Solar API Types ───────────────────────────────────────────────────

interface SolarRoofSegment {
  pitchDegrees: number;
  azimuthDegrees: number;
  areaMeters2: number;
  center: { lat: number; lng: number };
}

interface SolarBuildingData {
  roofSegments: SolarRoofSegment[];
  maxPanelsCount: number;
  maxSunshineHoursPerYear: number;
  imageryQuality: string;
  source: "google_solar_api" | "unavailable";
}

// ─── Gebäude ─────────────────────────────────────────────────────────────────

interface Building {
  id: string;
  latitude: number;
  longitude: number;
  buildingType: string;
  footprintM2: number | null;
  footprintPolygon?: LatLng[];
  heightM: number | null;
  roofSurfaces?: Array<{
    id: string;
    label: string;
    roofType: string;
    tiltDeg: number;
    azimuthDeg: number;
    areaM2: number;
    polygon?: LatLng[];
    // pvXpert-Dacheditor: zusätzliche Objekt-Parameter
    heightZ?: number | null;
    rotDeg?: number;
    scalePct?: number;
    projektion?: boolean;
    locked?: boolean;
    hidden?: boolean;
    semanticJson?: unknown; // Stage 2: { version, edges[], keepouts[] } — roh aus API
  }>;
}

const BUILDING_TYPE_LABELS: Record<string, string> = {
  residential_single: "Einfamilienhaus",
  residential_multi: "Mehrfamilienhaus",
  commercial: "Gewerbe",
  industrial: "Industrie",
  agricultural: "Landwirtschaft",
  public: "Öffentlich",
};

const ROOF_TYPE_LABELS: Record<string, string> = {
  pitched: "Schräg",
  flat: "Flach",
  carport: "Carport",
  facade: "Fassade",
};

interface BuildingFormState {
  address: string;
  latitude: number;
  longitude: number;
  buildingType: string;
  footprintM2: string;
  heightM: string;
}

// Hilfsfunktion: roofSurfaces eines Building in RoofSegment[] umwandeln
const SEGMENT_COLORS_LIST = ["#2563eb", "#16a34a", "#d97706", "#dc2626", "#7c3aed", "#0891b2"];
function buildingToSegments(building: Building): RoofSegment[] {
  return (building.roofSurfaces ?? []).map((rs, i) => ({
    id: rs.id,
    label: rs.label,
    tiltDeg: rs.tiltDeg,
    azimuthDeg: rs.azimuthDeg,
    areaM2: rs.areaM2,
    ...(rs.polygon ? { polygon: rs.polygon } : {}),
    color: SEGMENT_COLORS_LIST[i % SEGMENT_COLORS_LIST.length] ?? "#2563eb",
    isObstacle: rs.roofType === "obstacle",
    // pvXpert-Dacheditor-Felder durchreichen (additiv)
    ...(rs.heightZ !== undefined ? { heightZ: rs.heightZ } : {}),
    ...(rs.rotDeg !== undefined ? { rotDeg: rs.rotDeg } : {}),
    ...(rs.scalePct !== undefined ? { scalePct: rs.scalePct } : {}),
    ...(rs.projektion !== undefined ? { projektion: rs.projektion } : {}),
    ...(rs.locked !== undefined ? { locked: rs.locked } : {}),
    ...(rs.hidden !== undefined ? { hidden: rs.hidden } : {}),
    semanticJson: normalizeRoofSemanticData(rs.semanticJson),
  }));
}

// Selbstläufer: Gebäude-Grundriss aus VORHANDENEN Daten ableiten, damit der
// Editor nicht bei Null startet. Stufen (echte Übernahme vor Fabrikation):
//   1) gespeicherter footprintPolygon,
//   2) Bounding-Box der vorhandenen Dachflächen (Dächer liegen auf dem Gebäude),
//   3) Rechteck aus bekannter Grundfläche (footprintM2) am Standort.
// Rein als Startform (initialBuildingFootprint) — wird NICHT auto-persistiert,
// nur wenn der Nutzer sie im Editor bearbeitet.
const M_PER_DEG_LAT = 111320;
function deriveBuildingFootprint(building: Building): LatLng[] | null {
  if (building.footprintPolygon && building.footprintPolygon.length >= 3) {
    return building.footprintPolygon;
  }
  // 2) Aus Dachflächen-Polygonen ableiten (Bounding-Box + ~0,5 m Rand).
  const roofPts = (building.roofSurfaces ?? [])
    .flatMap((rs) => rs.polygon ?? [])
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (roofPts.length >= 3) {
    const lats = roofPts.map((p) => p.lat);
    const lngs = roofPts.map((p) => p.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const midLat = (minLat + maxLat) / 2;
    const mLat = 0.5 / M_PER_DEG_LAT;
    const mLng = 0.5 / (M_PER_DEG_LAT * Math.cos((midLat * Math.PI) / 180));
    return [
      { lat: maxLat + mLat, lng: minLng - mLng },
      { lat: maxLat + mLat, lng: maxLng + mLng },
      { lat: minLat - mLat, lng: maxLng + mLng },
      { lat: minLat - mLat, lng: minLng - mLng },
    ];
  }
  // 3) Rechteck aus bekannter Grundfläche (Seitenverhältnis 1,4:1) am Standort.
  if (
    building.footprintM2 &&
    building.footprintM2 > 0 &&
    Number.isFinite(building.latitude) &&
    Number.isFinite(building.longitude)
  ) {
    const ratio = 1.4;
    const halfWm = Math.sqrt(building.footprintM2 * ratio) / 2; // Ost-West
    const halfHm = Math.sqrt(building.footprintM2 / ratio) / 2; // Nord-Süd
    const dLat = halfHm / M_PER_DEG_LAT;
    const dLng = halfWm / (M_PER_DEG_LAT * Math.cos((building.latitude * Math.PI) / 180));
    const la = building.latitude;
    const lo = building.longitude;
    return [
      { lat: la + dLat, lng: lo - dLng },
      { lat: la + dLat, lng: lo + dLng },
      { lat: la - dLat, lng: lo + dLng },
      { lat: la - dLat, lng: lo - dLng },
    ];
  }
  return null;
}

interface RoofFormState {
  label: string;
  roofType: string;
  tiltDeg: number;
  azimuthDeg: number;
  areaM2: number;
}

// Browser-Download eines Text-Artefakts (DXF/GeoJSON) ohne externe Lib.
function downloadText(filename: string, text: string, mime: string) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Sammelt die Polygone eines Gebäudes (Grundriss/Dach/Störfläche) für den Export.
function collectExportPolys(building: Building) {
  const roofs = (building.roofSurfaces ?? [])
    .filter((r) => r.roofType !== "obstacle" && (r.polygon?.length ?? 0) >= 3)
    .map((r) => r.polygon as LatLng[]);
  const obstacles = (building.roofSurfaces ?? [])
    .filter((r) => r.roofType === "obstacle" && (r.polygon?.length ?? 0) >= 3)
    .map((r) => r.polygon as LatLng[]);
  const footprint =
    building.footprintPolygon && building.footprintPolygon.length >= 3
      ? building.footprintPolygon
      : undefined;
  return { roofs, obstacles, footprint };
}

function hasExportGeometry(building: Building): boolean {
  const { roofs, obstacles, footprint } = collectExportPolys(building);
  return !!footprint || roofs.length > 0 || obstacles.length > 0;
}

function exportBuilding(building: Building, fmt: "dxf" | "geojson") {
  const { roofs, obstacles, footprint } = collectExportPolys(building);
  if (fmt === "geojson") {
    const fc = roofDesignToGeoJSON({
      ...(footprint ? { footprint } : {}),
      roofs,
      obstacles,
    });
    downloadText(`dach-${building.id}.geojson`, JSON.stringify(fc, null, 2), "application/geo+json");
    return;
  }
  // DXF: alle Polygone mit gemeinsamem Zentroid auf lokale Meter projizieren.
  const groups: Array<{ layer: string; color: number; ll: LatLng[] }> = [];
  if (footprint) groups.push({ layer: "GEBAEUDE", color: 1, ll: footprint });
  roofs.forEach((r) => groups.push({ layer: "DACH", color: 5, ll: r }));
  obstacles.forEach((o) => groups.push({ layer: "STOERFLAECHE", color: 30, ll: o }));
  const { local } = latLngPolysToLocalMeters(groups.map((g) => g.ll));
  const polylines: DxfPolyline[] = groups.map((g, i) => ({
    layer: g.layer,
    color: g.color,
    points: local[i] ?? [],
  }));
  downloadText(`dach-${building.id}.dxf`, polygonsToDxf(polylines), "application/dxf");
}

function BuildingTab({ projectId }: { projectId: string }) {
  const [buildings, setBuildings] = useState<Building[]>([]);
  // Selbstläufer: abgeleitete Start-Grundrisse pro Gebäude (stabile Referenz →
  // resettet nicht buildingPts bei jedem Render; nur wenn sich buildings ändern).
  const derivedFootprints = useMemo(() => {
    const m = new Map<string, LatLng[]>();
    for (const b of buildings) {
      const fp = deriveBuildingFootprint(b);
      if (fp) m.set(b.id, fp);
    }
    return m;
  }, [buildings]);
  const [loading, setLoading] = useState(true);
  const [showBuildingForm, setShowBuildingForm] = useState(false);
  const [showRoofForm, setShowRoofForm] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState("");
  // RoofEditor: welches Gebäude hat den Editor offen
  const [editorBuildingId, setEditorBuildingId] = useState<string | null>(null);
  // Dachform-Selektor — pro Gebäude (buildingId → Dachform)
  const [dachformMap, setDachformMap] = useState<Record<string, Dachform>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Polygon-Zeichnen-State
  const [roofPolygon, setRoofPolygon] = useState<LatLng[]>([]);
  const [roofAreaFromMap, setRoofAreaFromMap] = useState(0);

  const [buildingForm, setBuildingForm] = useState<BuildingFormState>({
    address: "",
    latitude: 47.5,
    longitude: 13.0,
    buildingType: "residential_single",
    footprintM2: "",
    heightM: "",
  });

  const [geocodeHint, setGeocodeHint] = useState<string | null>(null);

  const [roofForm, setRoofForm] = useState<RoofFormState>({
    label: "",
    roofType: "pitched",
    tiltDeg: 30,
    azimuthDeg: 180,
    areaM2: 50,
  });

  // ─── Google Solar API State ──────────────────────────────────────────────────
  const [solarLoading, setSolarLoading] = useState<string | null>(null);
  const [solarData, setSolarData] = useState<Record<string, SolarBuildingData>>({});

  const loadSolarData = async (buildingId: string, lat: number, lng: number) => {
    setSolarLoading(buildingId);
    const res = await apiClient.get<SolarBuildingData>(
      `/geo/solar-building-data?lat=${lat}&lng=${lng}`,
    );
    if (res.success) setSolarData((d) => ({ ...d, [buildingId]: res.data }));
    setSolarLoading(null);
  };

  const applySegmentToForm = (seg: SolarRoofSegment) => {
    setRoofForm((f) => ({
      ...f,
      tiltDeg: Math.round(seg.pitchDegrees),
      azimuthDeg: Math.round(seg.azimuthDegrees),
      areaM2: Math.round(seg.areaMeters2),
    }));
  };
  // ────────────────────────────────────────────────────────────────────────────

  const loadBuildings = async () => {
    setLoading(true);
    const res = await apiClient.get<{ buildings: Building[] }>(`/projects/${projectId}/geospatial`);
    if (res.success) setBuildings(res.data.buildings ?? []);
    setLoading(false);
  };

  useEffect(() => {
    void loadBuildings();
  }, [projectId]);

  // Prüft, ob bereits ein Gebäude an (nahezu) gleicher Koordinate existiert
  // (~11 m Toleranz bei 0,0001°) → verhindert versehentliche Duplikate.
  const findDuplicateBuilding = (lat: number, lng: number): Building | undefined =>
    buildings.find(
      (b) =>
        b.latitude != null &&
        b.longitude != null &&
        Math.abs(b.latitude - lat) < 0.0001 &&
        Math.abs(b.longitude - lng) < 0.0001,
    );

  const handleCreateBuilding = async () => {
    // Duplikat-Warnung: gleiche Koordinate bereits vorhanden
    const dup = findDuplicateBuilding(buildingForm.latitude, buildingForm.longitude);
    if (dup) {
      const proceed = window.confirm(
        `An dieser Koordinate (${buildingForm.latitude.toFixed(4)}°N, ${buildingForm.longitude.toFixed(4)}°E) ` +
          `existiert bereits ein Gebäude. Trotzdem ein weiteres anlegen?`,
      );
      if (!proceed) return;
    }
    setSaving(true);
    setError(null);
    const body = {
      latitude: buildingForm.latitude,
      longitude: buildingForm.longitude,
      buildingType: buildingForm.buildingType,
      ...(buildingForm.footprintM2 ? { footprintM2: parseFloat(buildingForm.footprintM2) } : {}),
      ...(buildingForm.heightM ? { heightM: parseFloat(buildingForm.heightM) } : {}),
    };
    const res = await apiClient.post(`/projects/${projectId}/buildings`, body);
    if (res.success) {
      setShowBuildingForm(false);
      void loadBuildings();
    } else {
      setError("Gebäude konnte nicht erstellt werden.");
    }
    setSaving(false);
  };

  const handleDeleteBuilding = async (buildingId: string, label: string, roofCount: number) => {
    const msg =
      roofCount > 0
        ? `„${label}" mit ${roofCount} Dachfläche(n) wirklich löschen? Modulbelegungen und Hindernisse werden mitgelöscht.`
        : `„${label}" wirklich löschen?`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    setError(null);
    const res = await apiClient.delete(`/projects/${projectId}/buildings/${buildingId}`);
    if (res.success) {
      if (editorBuildingId === buildingId) setEditorBuildingId(null);
      void loadBuildings();
    } else {
      setError("Gebäude konnte nicht gelöscht werden.");
    }
    setSaving(false);
  };

  const handleCreateRoofSurface = async () => {
    if (!selectedBuildingId) return;
    // Wenn eine Fläche aus der Kartenzeichnung vorliegt, diese als areaM2 verwenden
    const effectiveArea = roofAreaFromMap > 0 ? roofAreaFromMap : roofForm.areaM2;
    // Client-Validierung mit konkreten Meldungen statt eines opaken 400.
    if (!Number.isFinite(roofForm.tiltDeg) || roofForm.tiltDeg < 0 || roofForm.tiltDeg > 90) {
      setError("Neigung muss eine Zahl zwischen 0 und 90° sein.");
      return;
    }
    if (!Number.isFinite(roofForm.azimuthDeg) || roofForm.azimuthDeg < 0 || roofForm.azimuthDeg > 360) {
      setError("Ausrichtung (Azimut) muss eine Zahl zwischen 0 und 360° sein.");
      return;
    }
    if (!Number.isFinite(effectiveArea) || effectiveArea <= 0) {
      setError("Bitte eine Fläche (m²) größer 0 angeben oder auf der Karte einzeichnen.");
      return;
    }
    setSaving(true);
    setError(null);
    // Bezeichnung ist optional — leeres Feld erhält einen sinnvollen Default,
    // damit das Pflichtfeld nicht zu einem stillen 400 führt.
    const label = roofForm.label.trim() || "Dachfläche";
    const res = await apiClient.post(
      `/projects/${projectId}/buildings/${selectedBuildingId}/roof-surfaces`,
      {
        ...roofForm,
        label,
        areaM2: effectiveArea,
        polygon: roofPolygon.length > 2 ? roofPolygon : [],
      },
    );
    if (res.success) {
      setShowRoofForm(false);
      setSelectedBuildingId("");
      setRoofPolygon([]);
      setRoofAreaFromMap(0);
      void loadBuildings();
    } else {
      setError(
        res.error?.message
          ? `Dachfläche konnte nicht erstellt werden: ${res.error.message}`
          : "Dachfläche konnte nicht erstellt werden.",
      );
    }
    setSaving(false);
  };

  // RoofEditor: Segment hinzufügen
  const handleEditorSegmentAdd = async (
    buildingId: string,
    seg: Omit<RoofSegment, "id">,
  ) => {
    setSaving(true);
    setError(null);
    const res = await apiClient.post(
      `/projects/${projectId}/buildings/${buildingId}/roof-surfaces`,
      {
        label: seg.label,
        roofType: seg.isObstacle ? "obstacle" : "pitched",
        tiltDeg: seg.tiltDeg,
        azimuthDeg: seg.azimuthDeg,
        areaM2: seg.areaM2,
        polygon: seg.polygon ?? [],
        ...(seg.heightZ !== undefined ? { heightZ: seg.heightZ } : {}),
        ...(seg.rotDeg !== undefined ? { rotDeg: seg.rotDeg } : {}),
        ...(seg.scalePct !== undefined ? { scalePct: seg.scalePct } : {}),
        ...(seg.projektion !== undefined ? { projektion: seg.projektion } : {}),
        ...(seg.locked !== undefined ? { locked: seg.locked } : {}),
        ...(seg.hidden !== undefined ? { hidden: seg.hidden } : {}),
        ...(seg.semanticJson !== undefined ? { semanticJson: seg.semanticJson } : {}),
      },
    );
    if (res.success) {
      void loadBuildings();
    } else {
      setError("Dachfläche konnte nicht erstellt werden.");
    }
    setSaving(false);
  };

  // RoofEditor: Segment löschen
  const handleEditorSegmentDelete = async (buildingId: string, segmentId: string) => {
    setSaving(true);
    setError(null);
    const res = await apiClient.delete(
      `/projects/${projectId}/buildings/${buildingId}/roof-surfaces/${segmentId}`,
    );
    if (res.success) {
      void loadBuildings();
    } else {
      setError("Dachfläche konnte nicht gelöscht werden.");
    }
    setSaving(false);
  };

  // RoofEditor: Gebäude-Grundriss persistieren (PUT /buildings/:id)
  const handleFootprintPersist = async (
    buildingId: string,
    footprint: LatLng[],
    areaM2: number,
  ) => {
    // Fläche: > 0 setzen, sonst null (Grundriss entfernt) → keine verwaiste footprintM2 ohne Polygon
    const footprintM2 = areaM2 > 0 ? areaM2 : null;
    // Optimistisches lokales Update
    setBuildings((prev) =>
      prev.map((b) =>
        b.id === buildingId ? { ...b, footprintPolygon: footprint, footprintM2 } : b,
      ),
    );
    const res = await apiClient.put(`/projects/${projectId}/buildings/${buildingId}`, {
      footprintPolygon: footprint,
      footprintM2,
    });
    if (!res.success) {
      setError("Gebäude-Grundriss konnte nicht gespeichert werden.");
    }
  };

  // RoofEditor: Segmente lokal aktualisieren UND geänderte Flächen im Backend
  // persistieren (PUT). Add/Delete laufen über eigene Handler — hier werden nur
  // bereits gespeicherte, GEÄNDERTE Flächen geschrieben; sonst gingen Tilt/
  // Azimut/Fläche/Label nach einem Reload verloren (war: nur UI-State).
  const handleEditorSegmentsChange = async (buildingId: string, segments: RoofSegment[]) => {
    const original = buildings.find((b) => b.id === buildingId)?.roofSurfaces ?? [];

    // Optimistisches lokales Update für sofortiges UI-Feedback.
    setBuildings((prev) =>
      prev.map((b) => {
        if (b.id !== buildingId) return b;
        return {
          ...b,
          roofSurfaces: segments.map((seg) => ({
            id: seg.id,
            label: seg.label,
            roofType: seg.isObstacle ? "obstacle" : "pitched",
            tiltDeg: seg.tiltDeg,
            azimuthDeg: seg.azimuthDeg,
            areaM2: seg.areaM2,
            ...(seg.polygon ? { polygon: seg.polygon } : {}),
            ...(seg.heightZ !== undefined ? { heightZ: seg.heightZ } : {}),
            ...(seg.rotDeg !== undefined ? { rotDeg: seg.rotDeg } : {}),
            ...(seg.scalePct !== undefined ? { scalePct: seg.scalePct } : {}),
            ...(seg.projektion !== undefined ? { projektion: seg.projektion } : {}),
            ...(seg.locked !== undefined ? { locked: seg.locked } : {}),
            ...(seg.hidden !== undefined ? { hidden: seg.hidden } : {}),
            ...(seg.semanticJson !== undefined ? { semanticJson: seg.semanticJson } : {}),
          })),
        };
      }),
    );

    for (const seg of segments) {
      const orig = original.find((s) => s.id === seg.id);
      if (!orig) continue; // neu → wird über handleEditorSegmentAdd angelegt
      const polygonChanged =
        JSON.stringify(orig.polygon ?? []) !== JSON.stringify(seg.polygon ?? []);
      const changed =
        orig.label !== seg.label ||
        orig.tiltDeg !== seg.tiltDeg ||
        orig.azimuthDeg !== seg.azimuthDeg ||
        orig.areaM2 !== seg.areaM2 ||
        (orig.heightZ ?? null) !== (seg.heightZ ?? null) ||
        (orig.rotDeg ?? 0) !== (seg.rotDeg ?? 0) ||
        (orig.scalePct ?? 100) !== (seg.scalePct ?? 100) ||
        (orig.projektion ?? false) !== (seg.projektion ?? false) ||
        (orig.locked ?? false) !== (seg.locked ?? false) ||
        (orig.hidden ?? false) !== (seg.hidden ?? false) ||
        JSON.stringify(normalizeRoofSemanticData(orig.semanticJson)) !==
          JSON.stringify(normalizeRoofSemanticData(seg.semanticJson)) ||
        polygonChanged;
      if (!changed) continue;
      const res = await apiClient.put(
        `/projects/${projectId}/buildings/${buildingId}/roof-surfaces/${seg.id}`,
        {
          label: seg.label,
          tiltDeg: seg.tiltDeg,
          azimuthDeg: seg.azimuthDeg,
          areaM2: seg.areaM2,
          ...(seg.polygon ? { polygon: seg.polygon } : {}),
          ...(seg.heightZ !== undefined ? { heightZ: seg.heightZ } : {}),
          ...(seg.rotDeg !== undefined ? { rotDeg: seg.rotDeg } : {}),
          ...(seg.scalePct !== undefined ? { scalePct: seg.scalePct } : {}),
          ...(seg.projektion !== undefined ? { projektion: seg.projektion } : {}),
          ...(seg.locked !== undefined ? { locked: seg.locked } : {}),
          ...(seg.hidden !== undefined ? { hidden: seg.hidden } : {}),
          ...(seg.semanticJson !== undefined ? { semanticJson: seg.semanticJson } : {}),
        },
      );
      if (!res.success) {
        setError(`Änderung an „${seg.label}" konnte nicht gespeichert werden.`);
        void loadBuildings(); // mit Server-Stand konsistent halten
        return;
      }
    }
  };

  if (loading) {
    return <div style={{ padding: 48, textAlign: "center", color: "var(--color-text-muted)" }}>Lade Gebäudedaten…</div>;
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Gebäude & Dachflächen</h2>
        <button className="btn-primary" onClick={() => setShowBuildingForm(!showBuildingForm)}>
          {showBuildingForm ? "Abbrechen" : "Gebäude hinzufügen"}
        </button>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, color: "#dc2626" }}>
          {error}
        </div>
      )}

      {/* Gebäude-Erstell-Formular */}
      {showBuildingForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Neues Gebäude</h3>
          {/* Adresse & Geocoding */}
          <div style={{ marginBottom: 12 }}>
            <FormField label="Adresse (Koordinaten werden automatisch übernommen)">
              <AddressAutocomplete
                value={buildingForm.address}
                onChange={(v) => {
                  setBuildingForm((f) => ({ ...f, address: v }));
                  setGeocodeHint(null);
                }}
                onSelect={(p) => {
                  setBuildingForm((f) => ({ ...f, address: p.label, latitude: p.lat, longitude: p.lng }));
                  setGeocodeHint(p.displayName);
                }}
                placeholder="Tippen, z.B. Musterstraße 1, 6020 Innsbruck"
                inputStyle={inputStyle}
              />
            </FormField>
            {geocodeHint && (
              <div style={{
                marginTop: 6,
                fontSize: 12,
                color: geocodeHint === "Keine Koordinaten gefunden." ? "#dc2626" : "#16a34a",
                background: geocodeHint === "Keine Koordinaten gefunden." ? "#fee2e2" : "#f0fdf4",
                border: `1px solid ${geocodeHint === "Keine Koordinaten gefunden." ? "#fca5a5" : "#86efac"}`,
                borderRadius: 4,
                padding: "4px 8px",
              }}>
                {geocodeHint}
              </div>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FormField label="Breitengrad (Latitude)">
              <input
                type="number"
                step="0.0001"
                value={buildingForm.latitude}
                onChange={(e) => setBuildingForm((f) => ({ ...f, latitude: parseFloat(e.target.value) }))}
                style={inputStyle}
              />
            </FormField>
            <FormField label="Längengrad (Longitude)">
              <input
                type="number"
                step="0.0001"
                value={buildingForm.longitude}
                onChange={(e) => setBuildingForm((f) => ({ ...f, longitude: parseFloat(e.target.value) }))}
                style={inputStyle}
              />
            </FormField>
            <FormField label="Gebäudetyp">
              <select
                value={buildingForm.buildingType}
                onChange={(e) => setBuildingForm((f) => ({ ...f, buildingType: e.target.value }))}
                style={inputStyle}
              >
                {Object.entries(BUILDING_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Grundfläche (m², optional)">
              <input
                type="number"
                min="0"
                value={buildingForm.footprintM2}
                onChange={(e) => setBuildingForm((f) => ({ ...f, footprintM2: e.target.value }))}
                placeholder="z.B. 120"
                style={inputStyle}
              />
            </FormField>
            <FormField label="Gebäudehöhe (m, optional)">
              <input
                type="number"
                min="0"
                value={buildingForm.heightM}
                onChange={(e) => setBuildingForm((f) => ({ ...f, heightM: e.target.value }))}
                placeholder="z.B. 8.5"
                style={inputStyle}
              />
            </FormField>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button className="btn-primary" onClick={() => void handleCreateBuilding()} disabled={saving}>
              {saving ? "Speichere…" : "Gebäude speichern"}
            </button>
            <button className="btn-secondary" onClick={() => setShowBuildingForm(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Dachflächen-Formular */}
      {showRoofForm && selectedBuildingId && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Neue Dachfläche</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <FormField label="Bezeichnung">
              <input
                type="text"
                value={roofForm.label}
                onChange={(e) => setRoofForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="z.B. Südseite"
                style={inputStyle}
              />
            </FormField>
            <FormField label="Dachtyp">
              <select
                value={roofForm.roofType}
                onChange={(e) => setRoofForm((f) => ({ ...f, roofType: e.target.value }))}
                style={inputStyle}
              >
                {Object.entries(ROOF_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Neigung (°, 0–90)">
              <input
                type="number"
                min="0"
                max="90"
                value={roofForm.tiltDeg}
                onChange={(e) => setRoofForm((f) => ({ ...f, tiltDeg: parseInt(e.target.value, 10) }))}
                style={inputStyle}
              />
            </FormField>
            <FormField label="Ausrichtung Azimut (°, 0–360)">
              <input
                type="number"
                min="0"
                max="360"
                value={roofForm.azimuthDeg}
                onChange={(e) => setRoofForm((f) => ({ ...f, azimuthDeg: parseInt(e.target.value, 10) }))}
                style={inputStyle}
              />
            </FormField>
            <FormField label="Fläche (m²)">
              <input
                type="number"
                min="1"
                value={roofAreaFromMap > 0 ? roofAreaFromMap : roofForm.areaM2}
                onChange={(e) => {
                  setRoofForm((f) => ({ ...f, areaM2: parseFloat(e.target.value) }));
                  setRoofAreaFromMap(0); // Manuelle Eingabe überschreibt Karten-Wert
                }}
                style={inputStyle}
              />
            </FormField>
          </div>

          {/* Dach abzeichnen */}
          <div style={{ marginTop: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Dachfläche auf Karte abzeichnen (optional)</h4>
            <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 8, margin: "0 0 8px 0" }}>
              Zeichnen Sie den Umriss der Dachfläche auf der Karte. Die Fläche wird automatisch berechnet und übernimmt das Feld „Fläche (m²)" oben.
            </p>
            <RoofDrawMap
              center={{
                lat: buildings.find((b) => b.id === selectedBuildingId)?.latitude ?? 47.2685,
                lng: buildings.find((b) => b.id === selectedBuildingId)?.longitude ?? 11.3943,
              }}
              {...(roofPolygon.length > 2 ? { initialPolygon: roofPolygon } : {})}
              onPolygonChange={(coords, areaM2) => {
                setRoofPolygon(coords);
                setRoofAreaFromMap(areaM2);
              }}
            />
            {roofAreaFromMap > 0 && (
              <p style={{ fontSize: 12, color: "#16a34a", marginTop: 8 }}>
                Fläche aus Zeichnung: {roofAreaFromMap.toLocaleString("de-AT")} m²
              </p>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button className="btn-primary" onClick={() => void handleCreateRoofSurface()} disabled={saving}>
              {saving ? "Speichere…" : "Dachfläche speichern"}
            </button>
            <button className="btn-secondary" onClick={() => {
              setShowRoofForm(false);
              setSelectedBuildingId("");
              setRoofPolygon([]);
              setRoofAreaFromMap(0);
            }}>Abbrechen</button>
          </div>
        </div>
      )}

      {/* Gebäude-Liste */}
      {buildings.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🏠</div>
          <div>Noch keine Gebäude erfasst. Fügen Sie ein Gebäude hinzu, um zu beginnen.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {buildings.map((building) => (
            <div key={building.id} className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {BUILDING_TYPE_LABELS[building.buildingType] ?? building.buildingType}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 2 }}>
                    {building.latitude.toFixed(4)}°N, {building.longitude.toFixed(4)}°E
                    {building.footprintM2 != null && ` · Grundfläche: ${building.footprintM2} m²`}
                    {building.heightM != null && ` · Höhe: ${building.heightM} m`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: 12 }}
                    onClick={() => {
                      setSelectedBuildingId(building.id);
                      setShowRoofForm(true);
                      setShowBuildingForm(false);
                    }}
                  >
                    Dachfläche hinzufügen
                  </button>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: 12, color: "#b91c1c", borderColor: "#fca5a5" }}
                    title="Gebäude löschen"
                    disabled={saving}
                    onClick={() =>
                      void handleDeleteBuilding(
                        building.id,
                        BUILDING_TYPE_LABELS[building.buildingType] ?? building.buildingType,
                        building.roofSurfaces?.length ?? 0,
                      )
                    }
                  >
                    🗑 Löschen
                  </button>
                </div>
              </div>

              {/* Google Solar API Panel */}
              {building.latitude && building.longitude && (() => {
                const solarEntry = solarData[building.id];
                return (
                  <div style={{ marginTop: 12, padding: 12, background: "#f0f9ff", borderRadius: 6, border: "1px solid #bae6fd" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>☀️ Google Solar API</span>
                      <button
                        className="btn-secondary"
                        onClick={() => void loadSolarData(building.id, building.latitude, building.longitude)}
                        disabled={solarLoading === building.id}
                        style={{ fontSize: 12 }}
                      >
                        {solarLoading === building.id ? "Lade…" : "Dachdaten abrufen"}
                      </button>
                    </div>
                    {solarEntry && solarEntry.source === "unavailable" && (
                      <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
                        Kein Google-Solar-API-Key konfiguriert (oder kein Gebäude erkannt).
                        Dachfläche manuell erfassen: „Dachfläche hinzufügen" oder „Dach-Editor öffnen".
                        Für automatische Erkennung GOOGLE_SOLAR_API_KEY am Server setzen.
                      </p>
                    )}
                    {solarEntry && solarEntry.source === "google_solar_api" && (
                      <div>
                        <p style={{ fontSize: 12, color: "#0369a1", margin: "0 0 8px 0" }}>
                          {solarEntry.roofSegments.length} Dachsegmente erkannt · max.{" "}
                          {solarEntry.maxPanelsCount} Module ·{" "}
                          {solarEntry.maxSunshineHoursPerYear} Sonnenstunden/Jahr
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {solarEntry.roofSegments.map((seg, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              gap: 12,
                              fontSize: 12,
                              padding: "4px 0",
                              borderBottom: "1px solid #e0f2fe",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ minWidth: 70 }}>Segment {i + 1}</span>
                            <span>Neigung: {seg.pitchDegrees.toFixed(0)}°</span>
                            <span>Ausrichtung: {seg.azimuthDegrees.toFixed(0)}°</span>
                            <span>Fläche: {seg.areaMeters2.toFixed(1)} m²</span>
                            <button
                              style={{ fontSize: 11, color: "#2563eb", border: "none", background: "none", cursor: "pointer", marginLeft: "auto" }}
                              onClick={() => {
                                applySegmentToForm(seg);
                                setSelectedBuildingId(building.id);
                                setShowRoofForm(true);
                                setShowBuildingForm(false);
                              }}
                            >
                              ↓ Übernehmen
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}

              {/* Dach-Editor Button */}
              <div style={{ marginTop: 12 }}>
                <button
                  className="btn-secondary"
                  style={{ fontSize: 12 }}
                  onClick={() =>
                    setEditorBuildingId(
                      editorBuildingId === building.id ? null : building.id,
                    )
                  }
                >
                  {editorBuildingId === building.id ? "Editor schließen" : "Dach-Editor öffnen"}
                </button>
                <button
                  className="btn-secondary"
                  style={{ fontSize: 12, marginLeft: 8 }}
                  disabled={!hasExportGeometry(building)}
                  title="Dachgeometrie als DXF (CAD) exportieren"
                  onClick={() => exportBuilding(building, "dxf")}
                >
                  ⬇ DXF
                </button>
                <button
                  className="btn-secondary"
                  style={{ fontSize: 12, marginLeft: 8 }}
                  disabled={!hasExportGeometry(building)}
                  title="Dachgeometrie als GeoJSON exportieren"
                  onClick={() => exportBuilding(building, "geojson")}
                >
                  ⬇ GeoJSON
                </button>
              </div>

              {/* RoofEditor — nur für das aktuell geöffnete Gebäude */}
              {editorBuildingId === building.id && (
                <div style={{ marginTop: 12 }}>
                  {/* Dachform-Selektor */}
                  <div className="card" style={{ marginBottom: 12, padding: 16 }}>
                    <DachformSelector
                      value={dachformMap[building.id] ?? "frei"}
                      onChange={(form, defaultTilt) => {
                        setDachformMap((m) => ({ ...m, [building.id]: form }));
                        // Dachform setzt die Default-Neigung + den Dachtyp für die
                        // nächste neue Dachfläche (vorher wurde defaultTilt verworfen).
                        const roofType = form === "flachdach" ? "flat" : form === "fassade" ? "facade" : "pitched";
                        setRoofForm((f) => ({ ...f, tiltDeg: defaultTilt, roofType }));
                      }}
                    />
                    <div
                      style={{
                        marginTop: 10,
                        padding: 8,
                        background: "#f0f9ff",
                        borderRadius: 6,
                        fontSize: 12,
                        color: "#0369a1",
                      }}
                    >
                      Neue Dachflächen übernehmen Neigung und Dachtyp dieser Dachform. Fläche im
                      Editor einzeichnen oder unter „Dachfläche hinzufügen" manuell eingeben.
                    </div>
                  </div>
                  <RoofEditor
                    buildingLat={building.latitude}
                    buildingLng={building.longitude}
                    segments={buildingToSegments(building)}
                    {...(derivedFootprints.get(building.id)
                      ? { initialBuildingFootprint: derivedFootprints.get(building.id)! }
                      : {})}
                    onSegmentsChange={(segs) =>
                      void handleEditorSegmentsChange(building.id, segs)
                    }
                    onSegmentAdd={(seg) =>
                      void handleEditorSegmentAdd(building.id, seg)
                    }
                    onSegmentDelete={(id) =>
                      void handleEditorSegmentDelete(building.id, id)
                    }
                    onBuildingFootprintChange={(footprint, areaM2) =>
                      void handleFootprintPersist(building.id, footprint, areaM2)
                    }
                    onClose={() => setEditorBuildingId(null)}
                  />
                </div>
              )}

              {/* Dachflächen-Tabelle (Kompaktansicht, wenn Editor zu) */}
              {editorBuildingId !== building.id && (
                <>
                  {building.roofSurfaces && building.roofSurfaces.length > 0 ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                          {["Bezeichnung", "Typ", "Neigung", "Azimut", "Fläche"].map((h) => (
                            <th key={h} style={{ textAlign: "left", padding: "6px 10px", fontSize: 12, color: "var(--color-text-muted)", fontWeight: 500 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {building.roofSurfaces.map((rs) => (
                          <tr key={rs.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                            <td style={{ padding: "8px 10px", fontWeight: 500 }}>{rs.label}</td>
                            <td style={{ padding: "8px 10px" }}>{ROOF_TYPE_LABELS[rs.roofType] ?? rs.roofType}</td>
                            <td style={{ padding: "8px 10px" }}>{rs.tiltDeg}°</td>
                            <td style={{ padding: "8px 10px" }}>{rs.azimuthDeg}°</td>
                            <td style={{ padding: "8px 10px" }}>{rs.areaM2} m²</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 13, color: "var(--color-text-muted)", fontStyle: "italic", padding: "8px 0" }}>
                      Noch keine Dachflächen erfasst. Dach-Editor öffnen zum Hinzufügen.
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Planung ──────────────────────────────────────────────────────────────────

interface RoofSurface {
  id: string;
  label: string;
  areaM2: number;
  /** Gezeichnetes Dachpolygon (≥ 3 Punkte) → echte Form für die Belegung. */
  polygon?: LatLng[];
  tiltDeg?: number;
  azimuthDeg?: number;
  /** Störflächen-Polygone (Kamin/Gaube) desselben Gebäudes. */
  obstacles?: LatLng[][];
  /** Sperrflächen-Polygone (semanticJson.keepouts) dieser Dachfläche. */
  keepouts?: LatLng[][];
  /** Gezeichnete Traufkante → Module liegen parallel zu dieser Dachseite (Süd etc.). */
  eave?: { a: LatLng; b: LatLng };
}

interface Placement {
  id: string;
  roofSurfaceId: string;
  roofSurfaceLabel?: string | undefined;
  modulesTotal: number;
  systemSizeKwp: number;
  createdAt: string;
}

interface LayoutModuleRect {
  xM: number;
  yM: number;
  wM: number;
  hM: number;
}

interface PlacementLayoutResult {
  modulesTotal: number;
  systemSizeKwp: number;
  modulesPerString: number;
  numberOfStrings: number;
  roofWidthM?: number | undefined;
  roofLengthM?: number | undefined;
  orientation?: string | undefined;
  modules?: LayoutModuleRect[] | undefined;
}

// Rohe API-Shapes (Engine-Begriffe) — werden auf die flachen UI-Typen gemappt.
// GET /planning liefert das Array direkt; POST …/layout liefert { placement, engineResult }.
interface RawPlacement {
  id: string;
  roofSurfaceId: string;
  quantity: number;
  createdAt: string;
  roofSurface?: { label?: string };
  layoutData?: { systemSizeKwp?: number };
}
interface RawLayoutData {
  modulesCount: number;
  systemSizeKwp: number;
  modulesPerString: number;
  numberOfStrings: number;
  roofWidthM?: number;
  roofLengthM?: number;
  orientation?: string;
  modules?: LayoutModuleRect[];
}

interface PlanningFormState {
  moduleWidthM: number;
  moduleLengthM: number;
  modulePeakWp: number;
}

function PlanningTab({ projectId, revisionId }: { projectId: string; revisionId: string }) {
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [roofSurfaces, setRoofSurfaces] = useState<RoofSurface[]>([]);
  // Manuelle Modulbearbeitung (#8): pro Dachfläche entfernte Modul-IDs.
  const [manualDisabled, setManualDisabled] = useState<Record<string, string[]>>({});
  const [selectedRoofId, setSelectedRoofId] = useState("");
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [layoutResult, setLayoutResult] = useState<PlacementLayoutResult | null>(null);
  const [bom, setBom] = useState<BillOfMaterials | null>(null);
  const [bomLoading, setBomLoading] = useState(false);
  const [showBomPrint, setShowBomPrint] = useState(false);

  const [form, setForm] = useState<PlanningFormState>({
    moduleWidthM: 1.13,
    moduleLengthM: 1.72,
    modulePeakWp: 440,
  });

  const loadData = async () => {
    setLoading(true);
    const [geoRes, planRes] = await Promise.all([
      apiClient.get<{ buildings: Building[] }>(`/projects/${projectId}/geospatial`),
      apiClient.get<RawPlacement[]>(`/projects/${projectId}/planning`),
    ]);
    if (geoRes.success) {
      const surfaces: RoofSurface[] = [];
      for (const building of geoRes.data.buildings ?? []) {
        const all = building.roofSurfaces ?? [];
        // Störflächen (Kamin/Gaube etc.) desselben Gebäudes als Hindernis-Polygone.
        const obstaclePolys = all
          .filter((rs) => rs.roofType === "obstacle" && rs.polygon && rs.polygon.length >= 3)
          .map((rs) => rs.polygon as LatLng[]);
        for (const rs of all) {
          // Störflächen sind keine belegbaren Dachflächen.
          if (rs.roofType === "obstacle") continue;
          // Sperrflächen + Traufe (semanticJson) dieser Fläche extrahieren.
          const sem = normalizeRoofSemanticData(rs.semanticJson);
          const keepoutPolys = sem.keepouts
            .map((k) => k.points)
            .filter((pts) => pts.length >= 3);
          // Traufkante → Modulreihen parallel zu dieser Dachseite (statt längster Kante).
          const traufeEdge = sem.edges.find((e) => e.type === "traufe" && e.points.length >= 2);
          const eave = traufeEdge
            ? { a: traufeEdge.points[0]!, b: traufeEdge.points[traufeEdge.points.length - 1]! }
            : undefined;
          surfaces.push({
            id: rs.id,
            label: rs.label,
            areaM2: rs.areaM2,
            ...(rs.polygon && rs.polygon.length >= 3 ? { polygon: rs.polygon } : {}),
            tiltDeg: rs.tiltDeg,
            azimuthDeg: rs.azimuthDeg,
            ...(obstaclePolys.length > 0 ? { obstacles: obstaclePolys } : {}),
            ...(keepoutPolys.length > 0 ? { keepouts: keepoutPolys } : {}),
            ...(eave ? { eave } : {}),
          });
        }
      }
      setRoofSurfaces(surfaces);
      const [firstSurface] = surfaces;
      if (firstSurface && !selectedRoofId) {
        setSelectedRoofId(firstSurface.id);
      }
    }
    if (planRes.success && Array.isArray(planRes.data)) {
      setPlacements(
        planRes.data.map((p) => ({
          id: p.id,
          roofSurfaceId: p.roofSurfaceId,
          roofSurfaceLabel: p.roofSurface?.label,
          modulesTotal: p.quantity,
          systemSizeKwp: typeof p.layoutData?.systemSizeKwp === "number" ? p.layoutData.systemSizeKwp : 0,
          createdAt: p.createdAt,
        })),
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    void loadData();
  }, [projectId]);

  const handleCalculate = async () => {
    if (!selectedRoofId) return;
    setCalculating(true);
    setError(null);
    setLayoutResult(null);
    const res = await apiClient.post<{ engineResult?: { data?: RawLayoutData } }>(
      `/projects/${projectId}/planning/roof-surfaces/${selectedRoofId}/layout`,
      { revisionId, ...form },
    );
    const d = res.success ? res.data?.engineResult?.data : undefined;
    if (d) {
      setLayoutResult({
        modulesTotal: d.modulesCount,
        systemSizeKwp: d.systemSizeKwp,
        modulesPerString: d.modulesPerString,
        numberOfStrings: d.numberOfStrings,
        roofWidthM: d.roofWidthM,
        roofLengthM: d.roofLengthM,
        orientation: d.orientation,
        modules: d.modules,
      });
      void loadData();
    } else {
      setError(
        res.success
          ? "Belegung berechnet, aber Ergebnis unerwartet — bitte Seite neu laden."
          : "Belegungsberechnung fehlgeschlagen. Bitte erneut versuchen.",
      );
    }
    setCalculating(false);
  };

  const handleDelete = async (placementId: string) => {
    setDeleting(placementId);
    await apiClient.delete(`/projects/${projectId}/planning/${placementId}`);
    void loadData();
    setDeleting(null);
  };

  const loadBom = async () => {
    setBomLoading(true);
    const res = await apiClient.get<BillOfMaterials>(`/projects/${projectId}/planning/bom`);
    if (res.success) {
      setBom(res.data);
    }
    setBomLoading(false);
  };

  const downloadBomCsv = () => {
    if (!bom) return;
    // de-AT-CSV (Semikolon-getrennt, Komma-Dezimal) via reine, getestete Funktion.
    // BOM (Byte Order Mark) voranstellen → Excel-AT erkennt UTF-8 + Umlaute korrekt.
    const csv = "﻿" + bomToCsv(bom);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = bomCsvFilename(projectId);
    a.click();
    URL.revokeObjectURL(url);
  };

  const roofLabel = (id: string) => roofSurfaces.find((r) => r.id === id)?.label ?? id;

  // Live-Vorschau (client-seitig, dieselbe roof-core-Engine wie der Server) —
  // zeigt sofort, was die gewählte Modulgröße auf der Dachfläche ergibt, bevor
  // „Belegung berechnen" das Ergebnis serverseitig persistiert.
  const selectedSurface = roofSurfaces.find((s) => s.id === selectedRoofId);
  const livePreview = (() => {
    if (!selectedSurface || !(selectedSurface.areaM2 > 0)) return null;
    if (!(form.moduleWidthM > 0) || !(form.moduleLengthM > 0)) return null;
    const { roofWidthM, roofLengthM } = rectFromArea(selectedSurface.areaM2);
    const grid = gridLayoutPlanar({
      roofWidthM,
      roofLengthM,
      moduleWidthM: form.moduleWidthM,
      moduleHeightM: form.moduleLengthM,
      orientation: "auto",
      setbackM: 0.3,
      moduleGapM: 0.02,
    });
    const kWp = (grid.moduleCount * (form.modulePeakWp || 0)) / 1000;
    return { roofWidthM, roofLengthM, grid, kWp };
  })();

  // Belegung auf der ECHTEN Dachform — nur wenn ein gezeichnetes Polygon (≥ 3
  // Punkte) vorliegt. Sonst bleibt es bei der Rechteck-Näherung (livePreview).
  const polygonBelegung = (() => {
    if (!selectedSurface?.polygon || selectedSurface.polygon.length < 3) return null;
    if (!(form.moduleWidthM > 0) || !(form.moduleLengthM > 0)) return null;
    const result = computeBelegung({
      polygon: selectedSurface.polygon,
      ...(selectedSurface.obstacles ? { obstacles: selectedSurface.obstacles } : {}),
      ...(selectedSurface.keepouts ? { keepouts: selectedSurface.keepouts } : {}),
      moduleSpec: { wp: form.modulePeakWp || 0, widthM: form.moduleWidthM, heightM: form.moduleLengthM },
      ...(selectedSurface.tiltDeg != null ? { tiltDeg: selectedSurface.tiltDeg } : {}),
      ...(selectedSurface.azimuthDeg != null ? { azimuthDeg: selectedSurface.azimuthDeg } : {}),
      ...(selectedSurface.eave ? { eave: selectedSurface.eave } : {}),
    });
    if (!result) return null;
    const kWp = (result.count * (form.modulePeakWp || 0)) / 1000;
    return { ...result, kWp };
  })();

  if (loading) {
    return <div style={{ padding: 48, textAlign: "center", color: "var(--color-text-muted)" }}>Lade Planungsdaten…</div>;
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Planung</h2>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, color: "#dc2626" }}>
          {error}
        </div>
      )}

      {roofSurfaces.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🏠</div>
          <div>Zuerst Gebäude und Dachflächen anlegen.</div>
        </div>
      ) : (
        <>
          {/* Belegungs-Formular */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Modulbelegung berechnen</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <FormField label="Dachfläche auswählen">
                <select
                  value={selectedRoofId}
                  onChange={(e) => setSelectedRoofId(e.target.value)}
                  style={inputStyle}
                >
                  {roofSurfaces.map((rs) => (
                    <option key={rs.id} value={rs.id}>
                      {rs.label} ({rs.areaM2} m²)
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Modulbreite (m)">
                <input
                  type="number"
                  min="0.1"
                  step="0.01"
                  value={form.moduleWidthM}
                  onChange={(e) => setForm((f) => ({ ...f, moduleWidthM: parseFloat(e.target.value) }))}
                  style={inputStyle}
                />
              </FormField>
              <FormField label="Modullänge (m)">
                <input
                  type="number"
                  min="0.1"
                  step="0.01"
                  value={form.moduleLengthM}
                  onChange={(e) => setForm((f) => ({ ...f, moduleLengthM: parseFloat(e.target.value) }))}
                  style={inputStyle}
                />
              </FormField>
              <FormField label="Modulleistung (Wp)">
                <input
                  type="number"
                  min="100"
                  step="5"
                  value={form.modulePeakWp}
                  onChange={(e) => setForm((f) => ({ ...f, modulePeakWp: parseInt(e.target.value, 10) }))}
                  style={inputStyle}
                />
              </FormField>
            </div>
            <button
              className="btn-primary"
              onClick={() => void handleCalculate()}
              disabled={calculating || !selectedRoofId || !revisionId}
            >
              {calculating ? "Berechne…" : "Belegung berechnen"}
            </button>
            {!revisionId && (
              <span style={{ marginLeft: 12, fontSize: 12, color: "var(--color-error)" }}>
                Keine Revision vorhanden
              </span>
            )}
          </div>

          {/* Modulbelegung — SVG-Vorschau (live) + Kennzahlen */}
          {(layoutResult || livePreview || polygonBelegung) && (() => {
            const persisted = layoutResult != null;
            // Echte Dachform bevorzugen, sobald ein gezeichnetes Polygon vorliegt
            // und noch kein serverseitig persistiertes Ergebnis angezeigt wird.
            const useShape = !persisted && polygonBelegung != null;
            const modules = layoutResult?.modules ?? livePreview?.grid.modules ?? [];
            const roofWidthM = layoutResult?.roofWidthM ?? livePreview?.roofWidthM ?? 0;
            const roofLengthM = layoutResult?.roofLengthM ?? livePreview?.roofLengthM ?? 0;
            // Manuelle Modulbearbeitung (#8): nur auf der echten Dachform (Live-Vorschau).
            const surfaceKey = selectedRoofId ?? "";
            const disabledIds = new Set(manualDisabled[surfaceKey] ?? []);
            const manualSummary =
              useShape && polygonBelegung
                ? summarizeManualLayout(polygonBelegung.moduleRectsLocal, disabledIds, form.modulePeakWp || 0)
                : null;
            const count = manualSummary
              ? manualSummary.count
              : useShape
                ? polygonBelegung.count
                : (layoutResult?.modulesTotal ?? livePreview?.grid.moduleCount ?? 0);
            const kWp = manualSummary
              ? manualSummary.kwp
              : useShape
                ? polygonBelegung.kWp
                : (layoutResult?.systemSizeKwp ?? livePreview?.kWp ?? 0);
            const toggleModule = (id: string) =>
              setManualDisabled((prev) => ({ ...prev, [surfaceKey]: [...toggleDisabledModule(new Set(prev[surfaceKey] ?? []), id)] }));
            const resetManual = () => setManualDisabled((prev) => ({ ...prev, [surfaceKey]: [] }));
            return (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600 }}>Modulbelegung</h3>
                  <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
                    {persisted
                      ? "berechnet & gespeichert"
                      : useShape
                        ? "echte Dachform — Live-Vorschau, noch nicht gespeichert"
                        : "Live-Vorschau — noch nicht gespeichert"}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
                  {useShape ? (
                    <RoofPolygonLayoutSvg
                      polygon={polygonBelegung.polygonLocal}
                      modules={polygonBelegung.moduleRectsLocal}
                      obstacles={polygonBelegung.obstaclesLocal}
                      keepouts={polygonBelegung.keepoutsLocal}
                      maxWidthPx={300}
                      disabledIds={disabledIds}
                      onToggleModule={toggleModule}
                    />
                  ) : (
                    <RoofLayoutSvg roofWidthM={roofWidthM} roofLengthM={roofLengthM} modules={modules} maxWidthPx={300} />
                  )}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, flex: 1, minWidth: 220 }}>
                    <KpiCard label="Modulanzahl" value={`${count}`} />
                    <KpiCard label="Systemgröße" value={`${num(kWp, 2)} kWp`} />
                    {layoutResult && <KpiCard label="Module/String" value={`${layoutResult.modulesPerString}`} />}
                    {layoutResult && <KpiCard label="Strings" value={`${layoutResult.numberOfStrings}`} />}
                  </div>
                </div>
                {count === 0 && (
                  <div style={{ marginTop: 10, fontSize: 12, color: "var(--color-warning, #92400e)" }}>
                    Keine Module platzierbar — Dachfläche zu klein für die gewählte Modulgröße.
                  </div>
                )}
                {manualSummary && (
                  <div style={{ marginTop: 10, fontSize: 12, color: "var(--color-text-muted)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <span>✏️ Einzelne Module anklicken, um sie zu entfernen oder wiederherzustellen.</span>
                    {manualSummary.removedCount > 0 && (
                      <>
                        <span style={{ color: "#b45309", fontWeight: 600 }}>
                          {manualSummary.removedCount} manuell entfernt
                        </span>
                        <button
                          type="button"
                          onClick={resetManual}
                          style={{ fontSize: 11, padding: "2px 8px", border: "1px solid var(--color-border)", borderRadius: 4, background: "var(--color-bg-subtle, #f1f5f9)", cursor: "pointer" }}
                        >
                          Zurücksetzen
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}

      {/* Bestehende Belegungen */}
      <div className="card" style={{ marginTop: roofSurfaces.length === 0 ? 16 : 0 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Bestehende Belegungen</h3>
        {placements.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: "var(--color-text-muted)", fontSize: 13 }}>
            Noch keine Belegungen berechnet.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                {["Dachfläche", "Module", "Systemgröße", "Erstellt", ""].map((h) => (
                  <th
                    key={h}
                    style={{ textAlign: "left", padding: "6px 10px", fontSize: 12, color: "var(--color-text-muted)", fontWeight: 500 }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {placements.map((p) => (
                <tr key={p.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "8px 10px", fontWeight: 500 }}>
                    {p.roofSurfaceLabel ?? roofLabel(p.roofSurfaceId)}
                  </td>
                  <td style={{ padding: "8px 10px" }}>{p.modulesTotal}</td>
                  <td style={{ padding: "8px 10px" }}>{num(p.systemSizeKwp, 2)} kWp</td>
                  <td style={{ padding: "8px 10px", color: "var(--color-text-muted)" }}>
                    {new Date(p.createdAt).toLocaleDateString("de-AT")}
                  </td>
                  <td style={{ padding: "8px 10px" }}>
                    <button
                      className="btn-secondary"
                      style={{ fontSize: 12, padding: "4px 10px" }}
                      onClick={() => void handleDelete(p.id)}
                      disabled={deleting === p.id}
                    >
                      {deleting === p.id ? "…" : "Löschen"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Stückliste */}
      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>Stückliste</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-secondary" onClick={() => void loadBom()} disabled={bomLoading}>
              {bomLoading ? "Generiere…" : "Stückliste generieren"}
            </button>
            {bom && (
              <>
                <button className="btn-secondary" onClick={downloadBomCsv}>
                  CSV (de-AT)
                </button>
                <button className="btn-secondary" onClick={() => setShowBomPrint(true)}>
                  🖨 Drucken / PDF
                </button>
              </>
            )}
          </div>
        </div>
        {bom && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f0f4ff" }}>
                <th style={{ padding: "6px 8px", textAlign: "left" }}>Pos.</th>
                <th style={{ padding: "6px 8px", textAlign: "left" }}>Bezeichnung</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Menge</th>
                <th style={{ padding: "6px 8px", textAlign: "left" }}>Einheit</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Gesamt (netto)</th>
              </tr>
            </thead>
            <tbody>
              {bom.lines.map((line) => (
                <tr key={line.position} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "5px 8px" }}>{line.position}</td>
                  <td style={{ padding: "5px 8px" }}>{line.description}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right" }}>{line.quantity}</td>
                  <td style={{ padding: "5px 8px" }}>{line.unit}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 500 }}>
                    {line.totalEurNet != null
                      ? `${line.totalEurNet.toLocaleString("de-AT", { maximumFractionDigits: 0 })} €`
                      : "–"}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid var(--color-border)", fontWeight: 700 }}>
                <td colSpan={4} style={{ padding: "6px 8px", textAlign: "right" }}>
                  Gesamt (netto):
                </td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>
                  {bom.totalEurNet.toLocaleString("de-AT", { maximumFractionDigits: 0 })} €
                </td>
              </tr>
            </tbody>
          </table>
        )}
        {!bom && !bomLoading && (
          <p style={{ color: "var(--color-text-muted)", fontSize: 13 }}>
            Zuerst Modulbelegung berechnen, dann Stückliste generieren.
          </p>
        )}
      </div>

      {/* Stückliste — Druckblatt (A4) */}
      {showBomPrint && bom && (
        <BomPrintSheet bom={bom} onClose={() => setShowBomPrint(false)} />
      )}
    </div>
  );
}

// ─── Simulation ───────────────────────────────────────────────────────────────

interface ShadingFormState {
  roofTiltDeg: number;
  roofAzimuthDeg: number;
  nearObstacleHeightM: string;
  nearObstacleDistanceM: string;
}

interface ShadingResultData {
  shadingFactor: number;
  annualLossPercent: number;
  monthlyFactors: number[];
  horizonObstruction: boolean;
  recommendation: string;
  confidenceScore: number;
}

interface ShadingEngineResult {
  status: string;
  data: ShadingResultData;
  confidenceScore: number;
  warnings: Array<{ code: string; message: string; severity: string }>;
}

interface ShadingApiResponse {
  run: unknown;
  engineResult: ShadingEngineResult;
}

interface SimFormState {
  systemSizeKwp: number;
  latitude: number;
  longitude: number;
  roofTiltDeg: number;
  roofAzimuthDeg: number;
  mode: string;
  /** "tmy-quick" = offline AT-TMY-Näherung | "pvgis-quick" = echter PVGIS-Aufruf */
  engine: string;
}

interface SimEngineData {
  annualYieldKwh: number;
  specificYieldKwhPerKwp: number;
  performanceRatio: number;
  monthlyYieldKwh: number[];
}

interface SimEngineResult {
  status: string;
  data: SimEngineData;
  confidenceScore: number;
  warnings: string[];
}

interface SimApiResponse {
  simulationRun: unknown;
  engineResult: SimEngineResult;
}

interface SurfaceSimResult {
  surfaceId: string;
  label: string;
  areaM2: number;
  usableAreaM2: number;
  tiltDeg: number;
  azimuthDeg: number;
  yieldFactor: number;
  modulesCount: number;
  systemKwp: number;
  annualYieldKwh: number;
  contributionPercent: number;
}

interface MultiSurfaceData {
  surfaces: SurfaceSimResult[];
  totalSystemKwp: number;
  totalAnnualYieldKwh: number;
  totalModules: number;
  specificYieldKwhPerKwp: number;
  weightedAzimuthDeg: number;
  weightedTiltDeg: number;
}

interface MultiSurfaceEngineResult {
  status: string;
  data: MultiSurfaceData | null;
  confidenceScore: number;
  warnings: Array<{ code: string; message: string; severity: string }>;
  assumptions: Array<{ field: string; assumed: unknown; reason: string }>;
}

interface MultiSurfaceApiResponse {
  run: unknown;
  engineResult: MultiSurfaceEngineResult;
}

interface SelfConsumptionResult {
  annualConsumptionKwh: number;
  systemKwp: number;
  annualYieldKwh: number;
  selfConsumptionKwh: number;
  selfConsumptionRate: number;
  autarkyRate: number;
  gridFeedInKwh: number;
  gridDrawKwh: number;
  batteryRecommendationKwh: number;
  withBatteryAutarkyRate: number;
  withBatterySelfConsumptionRate: number;
  selfConsumptionValueEur: number;
  feedInValueEur: number;
  totalAnnualValueEur: number;
  optimalSystemKwp: number;
  optimalSystemNote: string;
}

interface SelfConsumptionEngineResult {
  status: string;
  data: SelfConsumptionResult | null;
  confidenceScore: number;
  warnings: Array<{ code: string; message: string; severity: string }>;
}

interface SelfConsumptionApiResponse {
  run: unknown;
  engineResult: SelfConsumptionEngineResult;
}

function SimulationTab({ projectId, revisionId }: { projectId: string; revisionId: string }) {
  const [form, setForm] = useState<SimFormState>({
    systemSizeKwp: 10,
    latitude: 47.5,
    longitude: 13.0,
    roofTiltDeg: 30,
    roofAzimuthDeg: 180,
    mode: "quick",
    engine: "tmy-quick",
  });
  const [result, setResult] = useState<SimEngineResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── PVGIS State ────────────────────────────────────────────────────────────
  const { fetchPvgis, pvgisData, pvgisLoading } = usePvgis();
  const [pvgisBuildingCoords, setPvgisBuildingCoords] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await apiClient.get<{ buildings: Building[] }>(`/projects/${projectId}/geospatial`);
      if (res.success) {
        const firstBuilding = (res.data.buildings ?? []).find(
          (b) => b.latitude && b.longitude,
        );
        if (firstBuilding) {
          setPvgisBuildingCoords({ lat: firstBuilding.latitude, lng: firstBuilding.longitude });
          // Echte Standort-Koordinaten ins Sim-Formular übernehmen, statt der
          // Default-Mitte (47.5/13.0) — nur solange der Nutzer nichts geändert hat.
          setForm((f) =>
            f.latitude === 47.5 && f.longitude === 13.0
              ? { ...f, latitude: firstBuilding.latitude, longitude: firstBuilding.longitude }
              : f,
          );
        }
      }
    })();
    // Anlagengröße aus dem Dachentwurf vorbefüllen (nur solange Default).
    void apiClient
      .get<Array<{ summaryJson?: { result?: { estimatedKwp?: number } } }>>(`/projects/${projectId}/roof-designs`)
      .then((r) => {
        if (r.success && Array.isArray(r.data) && r.data.length > 0) {
          const k = r.data[0]?.summaryJson?.result?.estimatedKwp;
          if (typeof k === "number" && k > 0) {
            setForm((f) => (f.systemSizeKwp === 10 ? { ...f, systemSizeKwp: k } : f));
          }
        }
      });
  }, [projectId]);

  // ── Mehrdach-Simulation State ──────────────────────────────────────────────
  const [multiSim, setMultiSim] = useState<MultiSurfaceData | null>(null);
  const [multiSimLoading, setMultiSimLoading] = useState(false);
  const [multiSimError, setMultiSimError] = useState<string | null>(null);
  const [multiSimResult, setMultiSimResult] = useState<MultiSurfaceEngineResult | null>(null);

  // ── Eigenverbrauch-Optimierung State ──────────────────────────────────────
  const [selfConsForm, setSelfConsForm] = useState({
    annualConsumptionKwh: 4500,
    hasBattery: false,
    batteryKwh: 10,
  });
  const [selfConsResult, setSelfConsResult] =
    useState<SelfConsumptionResult | null>(null);
  const [selfConsLoading, setSelfConsLoading] = useState(false);
  const [selfConsError, setSelfConsError] = useState<string | null>(null);

  // ── Verschattungsanalyse State ─────────────────────────────────────────────
  const [shadingForm, setShadingForm] = useState<ShadingFormState>({
    roofTiltDeg: 30,
    roofAzimuthDeg: 180,
    nearObstacleHeightM: "",
    nearObstacleDistanceM: "",
  });
  const [shadingResult, setShadingResult] = useState<ShadingEngineResult | null>(null);
  const [shadingRunning, setShadingRunning] = useState(false);
  const [shadingError, setShadingError] = useState<string | null>(null);

  const handleStart = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    const res = await apiClient.post<SimApiResponse>(`/projects/${projectId}/simulation`, {
      revisionId,
      ...form,
    });
    if (res.success && res.data?.engineResult) {
      const er = res.data.engineResult;
      setResult(er);
      // PVGIS/Engine kann needs_user_review mit leeren Daten liefern → sonst
      // zeigt die UI nichts und keinen Fehler. Dann die Engine-Warnung zeigen.
      if (!er.data) {
        const w = (er as { warnings?: Array<string | { message?: string }> }).warnings?.[0];
        const msg = typeof w === "string" ? w : w?.message;
        setError(msg ?? `Simulation lieferte kein Ergebnis (Status: ${(er as { status?: string }).status ?? "unbekannt"}). Bei PVGIS ggf. „Schnell" (TMY) wählen.`);
      }
    } else {
      setError("Simulation fehlgeschlagen. Bitte erneut versuchen.");
    }
    setRunning(false);
  };

  const handleShading = async () => {
    setShadingRunning(true);
    setShadingError(null);
    setShadingResult(null);
    const body: Record<string, unknown> = {
      revisionId,
      roofTiltDeg: shadingForm.roofTiltDeg,
      roofAzimuthDeg: shadingForm.roofAzimuthDeg,
    };
    if (shadingForm.nearObstacleHeightM !== "") {
      body.nearObstacleHeightM = parseFloat(shadingForm.nearObstacleHeightM);
    }
    if (shadingForm.nearObstacleDistanceM !== "") {
      body.nearObstacleDistanceM = parseFloat(shadingForm.nearObstacleDistanceM);
    }
    const res = await apiClient.post<ShadingApiResponse>(
      `/projects/${projectId}/simulation/shading`,
      body,
    );
    if (res.success && res.data?.engineResult) {
      setShadingResult(res.data.engineResult);
    } else {
      setShadingError("Verschattungsanalyse fehlgeschlagen. Bitte erneut versuchen.");
    }
    setShadingRunning(false);
  };

  const handleMultiSim = async () => {
    setMultiSimLoading(true);
    setMultiSimError(null);
    setMultiSim(null);
    setMultiSimResult(null);
    const res = await apiClient.post<MultiSurfaceApiResponse>(`/projects/${projectId}/multi-surface`, {});
    if (res.success && res.data?.engineResult) {
      setMultiSimResult(res.data.engineResult);
      if (res.data.engineResult.data) {
        setMultiSim(res.data.engineResult.data);
      }
    } else {
      setMultiSimError("Mehrdach-Simulation fehlgeschlagen. Bitte erneut versuchen.");
    }
    setMultiSimLoading(false);
  };

  const handleSelfCons = async () => {
    setSelfConsLoading(true);
    setSelfConsError(null);
    setSelfConsResult(null);
    const res = await apiClient.post<SelfConsumptionApiResponse>(
      `/projects/${projectId}/self-consumption`,
      selfConsForm,
    );
    if (res.success && res.data?.engineResult?.data) {
      setSelfConsResult(res.data.engineResult.data);
    } else {
      setSelfConsError(
        "Berechnung fehlgeschlagen. Bitte erneut versuchen.",
      );
    }
    setSelfConsLoading(false);
  };

  const engineData = result?.data;
  const monthly = engineData?.monthlyYieldKwh ?? [];
  const maxMonthly = monthly.length > 0 ? Math.max(...monthly) : 1;

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Simulation</h2>
      </div>

      {/* Formular */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Simulationsparameter</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <FormField label="Anlagengröße (kWp)">
            <input
              type="number"
              min="0.5"
              step="0.5"
              value={form.systemSizeKwp}
              onChange={(e) => setForm((f) => ({ ...f, systemSizeKwp: parseFloat(e.target.value) }))}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Breitengrad">
            <input
              type="number"
              step="0.0001"
              value={form.latitude}
              onChange={(e) => setForm((f) => ({ ...f, latitude: parseFloat(e.target.value) }))}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Längengrad">
            <input
              type="number"
              step="0.0001"
              value={form.longitude}
              onChange={(e) => setForm((f) => ({ ...f, longitude: parseFloat(e.target.value) }))}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Dachneigung (°)">
            <input
              type="number"
              min="0"
              max="90"
              value={form.roofTiltDeg}
              onChange={(e) => setForm((f) => ({ ...f, roofTiltDeg: parseInt(e.target.value, 10) }))}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Dachausrichtung Azimut (°)">
            <input
              type="number"
              min="0"
              max="360"
              value={form.roofAzimuthDeg}
              onChange={(e) => setForm((f) => ({ ...f, roofAzimuthDeg: parseInt(e.target.value, 10) }))}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Modus">
            <select
              value={form.mode}
              onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}
              style={inputStyle}
            >
              <option value="quick">Schnell</option>
              <option value="precision">Detailliert</option>
            </select>
          </FormField>
          <FormField label="Wetterdaten">
            <select
              value={form.engine}
              onChange={(e) => setForm((f) => ({ ...f, engine: e.target.value }))}
              style={inputStyle}
              data-testid="sim-engine-select"
            >
              <option value="tmy-quick">TMY-Näherung (schnell, offline)</option>
              <option value="pvgis-quick">PVGIS (echt, standortbezogen)</option>
            </select>
          </FormField>
        </div>
        <div style={{ marginTop: 16 }}>
          <button
            data-testid="start-simulation"
            className="btn-primary"
            onClick={() => void handleStart()}
            disabled={running || !revisionId}
          >
            {running ? "Simulation läuft…" : "Simulation starten"}
          </button>
          {!revisionId && (
            <span style={{ marginLeft: 12, fontSize: 12, color: "var(--color-error)" }}>
              Keine Revision vorhanden
            </span>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, color: "#dc2626" }}>
          {error}
        </div>
      )}

      {/* Ergebnisse */}
      {engineData && (
        <div>
          {/* KPI-Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <KpiCard label="Jahresertrag" value={`${num(engineData.annualYieldKwh)} kWh`} />
            <KpiCard label="Spezif. Ertrag" value={`${num(engineData.specificYieldKwhPerKwp)} kWh/kWp`} />
            <KpiCard label="Performance Ratio" value={pct(engineData.performanceRatio * 100, 1)} />
          </div>

          {/* Monatsdiagramm */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Monatlicher Ertrag</h3>
            <div style={{ display: "flex", alignItems: "flex-end", height: 120, gap: 2, padding: "8px 0" }}>
              {monthly.map((val, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    backgroundColor: "var(--color-primary)",
                    height: `${(val / maxMonthly) * 100}%`,
                    minHeight: 2,
                    borderRadius: "2px 2px 0 0",
                    opacity: 0.85,
                  }}
                  title={`${MONTH_NAMES[i]}: ${val.toLocaleString("de-AT")} kWh`}
                />
              ))}
            </div>
            <div style={{ display: "flex", gap: 2 }}>
              {MONTH_NAMES.map((m) => (
                <div key={m} style={{ flex: 1, textAlign: "center", fontSize: 10, color: "var(--color-text-muted)", paddingTop: 4 }}>
                  {m}
                </div>
              ))}
            </div>
          </div>

          {/* Konfidenz */}
          {result.confidenceScore != null && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                <span style={{ fontWeight: 500 }}>Konfidenz-Score</span>
                <span style={{ color: "var(--color-text-muted)" }}>{pct(result.confidenceScore * 100, 0)}</span>
              </div>
              <div style={{ background: "var(--color-border)", borderRadius: 4, height: 8, overflow: "hidden" }}>
                <div
                  style={{
                    width: `${result.confidenceScore * 100}%`,
                    height: "100%",
                    backgroundColor: result.confidenceScore >= 0.7 ? "var(--color-success)" : result.confidenceScore >= 0.4 ? "var(--color-warning)" : "var(--color-error)",
                    borderRadius: 4,
                    transition: "width 0.4s",
                  }}
                />
              </div>
            </div>
          )}

          {/* Warnungen */}
          {result.warnings && result.warnings.length > 0 && (
            <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 6, padding: 12, fontSize: 13 }}>
              <strong>Hinweise:</strong>
              <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                {result.warnings.map((w, i) => (
                  <li key={i}>{typeof w === "string" ? w : (w as { message?: string })?.message ?? JSON.stringify(w)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!engineData && !running && !error && (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>
          Noch kein Simulationslauf. Starten Sie eine Simulation, um Ergebnisse zu sehen.
        </div>
      )}

      {/* ── Verschattungsanalyse ─────────────────────────────────────────── */}
      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>🌑 Verschattungsanalyse</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FormField label="Dachneigung (°, 0–90)">
            <input
              type="number"
              min="0"
              max="90"
              value={shadingForm.roofTiltDeg}
              onChange={(e) => setShadingForm((f) => ({ ...f, roofTiltDeg: parseInt(e.target.value, 10) }))}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Ausrichtung Azimut (°, 0=N, 180=S)">
            <input
              type="number"
              min="0"
              max="360"
              value={shadingForm.roofAzimuthDeg}
              onChange={(e) => setShadingForm((f) => ({ ...f, roofAzimuthDeg: parseInt(e.target.value, 10) }))}
              style={inputStyle}
            />
          </FormField>
          <FormField label="Hindernis-Höhe (m, optional)">
            <input
              type="number"
              min="0"
              step="0.5"
              value={shadingForm.nearObstacleHeightM}
              onChange={(e) => setShadingForm((f) => ({ ...f, nearObstacleHeightM: e.target.value }))}
              placeholder="z.B. 6"
              style={inputStyle}
            />
          </FormField>
          <FormField label="Hindernis-Abstand (m, optional)">
            <input
              type="number"
              min="0"
              step="0.5"
              value={shadingForm.nearObstacleDistanceM}
              onChange={(e) => setShadingForm((f) => ({ ...f, nearObstacleDistanceM: e.target.value }))}
              placeholder="z.B. 4"
              style={inputStyle}
            />
          </FormField>
        </div>

        <div style={{ marginTop: 16 }}>
          <button
            className="btn-primary"
            onClick={() => void handleShading()}
            disabled={shadingRunning || !revisionId}
          >
            {shadingRunning ? "Berechne…" : "Verschattung berechnen"}
          </button>
          {!revisionId && (
            <span style={{ marginLeft: 12, fontSize: 12, color: "var(--color-error)" }}>
              Keine Revision vorhanden
            </span>
          )}
        </div>

        {shadingError && (
          <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginTop: 12, fontSize: 13, color: "#dc2626" }}>
            {shadingError}
          </div>
        )}

        {shadingResult?.data && (() => {
          const d = shadingResult.data;
          const shadingPct = Math.round(d.shadingFactor * 100);
          const barColor = shadingPct >= 90 ? "var(--color-success)" : shadingPct >= 60 ? "var(--color-warning)" : "var(--color-error)";
          return (
            <div style={{ marginTop: 16 }}>
              {/* Shadingfaktor-Balken */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                  <span style={{ fontWeight: 500 }}>Shadingfaktor</span>
                  <span style={{ color: barColor, fontWeight: 600 }}>{shadingPct} %</span>
                </div>
                <div style={{ background: "var(--color-border)", borderRadius: 4, height: 10, overflow: "hidden" }}>
                  <div
                    style={{
                      width: `${shadingPct}%`,
                      height: "100%",
                      backgroundColor: barColor,
                      borderRadius: 4,
                      transition: "width 0.4s",
                    }}
                  />
                </div>
              </div>

              {/* KPIs */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <KpiCard label="Jährl. Verlust" value={pct(d.annualLossPercent, 1)} />
                <KpiCard label="Horizont-Hindernis" value={d.horizonObstruction ? "Ja" : "Nein"} />
                <KpiCard label="Konfidenz" value={pct(shadingResult.confidenceScore * 100, 0)} />
              </div>

              {/* Empfehlung */}
              <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, padding: 12, fontSize: 13, color: "#0369a1" }}>
                <strong>Empfehlung:</strong> {d.recommendation}
              </div>

              {/* Warnungen */}
              {shadingResult.warnings && shadingResult.warnings.length > 0 && (
                <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 6, padding: 12, marginTop: 8, fontSize: 13 }}>
                  <strong>Hinweise:</strong>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                    {shadingResult.warnings.map((w, i) => (
                      <li key={i}>{w.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })()}

        {!shadingResult && !shadingRunning && !shadingError && (
          <div style={{ textAlign: "center", padding: 24, color: "var(--color-text-muted)", fontSize: 13 }}>
            Noch keine Verschattungsanalyse. Parameter eingeben und berechnen.
          </div>
        )}
      </div>

      {/* ── Mehrdach-Simulation ──────────────────────────────────────────────── */}
      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Mehrdach-Simulation</h3>
        <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginBottom: 12 }}>
          Kombiniert alle Dachflächen des Projekts und berechnet den Gesamt-Solarertrag gewichtet nach Neigung und Ausrichtung.
        </p>
        <div>
          <button
            className="btn-primary"
            onClick={() => void handleMultiSim()}
            disabled={multiSimLoading}
          >
            {multiSimLoading ? "Berechne…" : "Mehrdach-Simulation starten"}
          </button>
        </div>

        {multiSimError && (
          <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginTop: 12, fontSize: 13, color: "#dc2626" }}>
            {multiSimError}
          </div>
        )}

        {multiSim && (
          <div style={{ marginTop: 16 }}>
            {/* Gesamt-KPIs */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
              <KpiCard label="Gesamt-kWp" value={`${multiSim.totalSystemKwp.toLocaleString("de-AT")} kWp`} />
              <KpiCard label="Jahresertrag" value={`${multiSim.totalAnnualYieldKwh.toLocaleString("de-AT")} kWh`} />
              <KpiCard label="Module gesamt" value={`${multiSim.totalModules}`} />
              <KpiCard label="Spezif. Ertrag" value={`${multiSim.specificYieldKwhPerKwp.toLocaleString("de-AT")} kWh/kWp`} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <KpiCard label="Gew. Azimut" value={`${multiSim.weightedAzimuthDeg}°`} />
              <KpiCard label="Gew. Neigung" value={`${multiSim.weightedTiltDeg}°`} />
            </div>

            {/* Tabelle je Dachfläche */}
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Ergebnis je Dachfläche</h4>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f0f4ff" }}>
                    <th style={{ padding: "6px 8px", textAlign: "left" }}>Fläche</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Fläche m²</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Nutzbar m²</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Neigung</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Azimut</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Ertragsfaktor</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Module</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>kWp</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>kWh/Jahr</th>
                    <th style={{ padding: "6px 8px", textAlign: "right" }}>Anteil</th>
                  </tr>
                </thead>
                <tbody>
                  {multiSim.surfaces.map((s) => (
                    <tr key={s.surfaceId} style={{ borderBottom: "1px solid var(--color-border)" }}>
                      <td style={{ padding: "5px 8px", fontWeight: 500 }}>{s.label}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right" }}>{num(s.areaM2, 1)}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right" }}>{num(s.usableAreaM2, 1)}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right" }}>{s.tiltDeg}°</td>
                      <td style={{ padding: "5px 8px", textAlign: "right" }}>{s.azimuthDeg}°</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", color: s.yieldFactor >= 0.8 ? "var(--color-success)" : s.yieldFactor >= 0.65 ? "var(--color-warning)" : "var(--color-error)" }}>
                        {pct(s.yieldFactor * 100, 0)}
                      </td>
                      <td style={{ padding: "5px 8px", textAlign: "right" }}>{s.modulesCount}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right" }}>{num(s.systemKwp, 2)}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right" }}>{Math.round(s.annualYieldKwh).toLocaleString("de-AT")}</td>
                      <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 500 }}>{s.contributionPercent} %</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Konfidenz & Warnungen */}
            {multiSimResult && (
              <>
                {multiSimResult.confidenceScore != null && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                      <span style={{ fontWeight: 500 }}>Konfidenz-Score</span>
                      <span style={{ color: "var(--color-text-muted)" }}>{pct(multiSimResult.confidenceScore * 100, 0)}</span>
                    </div>
                    <div style={{ background: "var(--color-border)", borderRadius: 4, height: 8, overflow: "hidden" }}>
                      <div style={{ width: `${multiSimResult.confidenceScore * 100}%`, height: "100%", backgroundColor: "var(--color-primary)", borderRadius: 4, transition: "width 0.4s" }} />
                    </div>
                  </div>
                )}
                {multiSimResult.warnings && multiSimResult.warnings.length > 0 && (
                  <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 6, padding: 12, marginTop: 8, fontSize: 13 }}>
                    <strong>Hinweise:</strong>
                    <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                      {multiSimResult.warnings.map((w, i) => (
                        <li key={i}>{w.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!multiSim && !multiSimLoading && !multiSimError && (
          <div style={{ textAlign: "center", padding: 24, color: "var(--color-text-muted)", fontSize: 13 }}>
            Noch keine Mehrdach-Simulation. Starten Sie die Berechnung, um alle Dachflächen zu kombinieren.
          </div>
        )}
      </div>

      {/* ── PVGIS Standort-Analyse ──────────────────────────────────────────── */}
      <PvgisPanel
        coords={pvgisBuildingCoords}
        pvgisData={pvgisData}
        pvgisLoading={pvgisLoading}
        onFetch={fetchPvgis}
      />

      {/* ── Eigenverbrauch-Optimierung ───────────────────────────────────────── */}
      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Eigenverbrauch-Optimierung</h3>
        <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginBottom: 12 }}>
          Berechnet Eigenverbrauchsquote, Autarkiegrad und optimale Batteriegröße auf Basis des Jahresverbrauchs.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <FormField label="Jahresverbrauch (kWh)">
            <input
              type="number"
              min="100"
              step="100"
              value={selfConsForm.annualConsumptionKwh}
              onChange={(e) =>
                setSelfConsForm((f) => ({
                  ...f,
                  annualConsumptionKwh: parseFloat(e.target.value),
                }))
              }
              style={inputStyle}
            />
          </FormField>
          <FormField label="Batterie">
            <label style={{ display: "flex", alignItems: "center", gap: 8, height: 34 }}>
              <input
                type="checkbox"
                checked={selfConsForm.hasBattery}
                onChange={(e) =>
                  setSelfConsForm((f) => ({ ...f, hasBattery: e.target.checked }))
                }
              />
              <span style={{ fontSize: 13 }}>Batteriespeicher vorhanden</span>
            </label>
          </FormField>
          {selfConsForm.hasBattery && (
            <FormField label="Batteriekapazität (kWh)">
              <input
                type="number"
                min="1"
                step="1"
                value={selfConsForm.batteryKwh}
                onChange={(e) =>
                  setSelfConsForm((f) => ({
                    ...f,
                    batteryKwh: parseFloat(e.target.value),
                  }))
                }
                style={inputStyle}
              />
            </FormField>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <button
            className="btn-primary"
            onClick={() => void handleSelfCons()}
            disabled={selfConsLoading || !revisionId}
          >
            {selfConsLoading ? "Berechne…" : "Eigenverbrauch berechnen"}
          </button>
          {!revisionId && (
            <span style={{ marginLeft: 12, fontSize: 12, color: "var(--color-error)" }}>
              Keine Revision vorhanden
            </span>
          )}
        </div>

        {selfConsError && (
          <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginTop: 12, fontSize: 13, color: "#dc2626" }}>
            {selfConsError}
          </div>
        )}

        {selfConsResult && (
          <div style={{ marginTop: 16 }}>
            {/* Haupt-KPIs */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
              <KpiCard
                label="Eigenverbrauchsquote"
                value={`${Math.round(selfConsResult.selfConsumptionRate * 100)} %`}
              />
              <KpiCard
                label="Autarkiegrad"
                value={`${Math.round(selfConsResult.autarkyRate * 100)} %`}
              />
              <KpiCard
                label="Autarkie mit Batterie"
                value={`${Math.round(selfConsResult.withBatteryAutarkyRate * 100)} %`}
              />
              <KpiCard
                label="Jährl. Nutzen"
                value={eur(selfConsResult.totalAnnualValueEur)}
              />
            </div>

            {/* Energieflüsse */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
              <KpiCard
                label="Eigenverbrauch"
                value={`${selfConsResult.selfConsumptionKwh.toLocaleString("de-AT")} kWh`}
              />
              <KpiCard
                label="Netzbezug"
                value={`${selfConsResult.gridDrawKwh.toLocaleString("de-AT")} kWh`}
              />
              <KpiCard
                label="Einspeisung"
                value={`${selfConsResult.gridFeedInKwh.toLocaleString("de-AT")} kWh`}
              />
            </div>

            {/* Wirtschaftlichkeit */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <KpiCard
                label="Eigenverbrauchswert"
                value={eur(selfConsResult.selfConsumptionValueEur)}
              />
              <KpiCard
                label="Einspeisevergütung"
                value={eur(selfConsResult.feedInValueEur)}
              />
            </div>

            {/* Batterie-Empfehlung */}
            <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, padding: 12, marginBottom: 12, fontSize: 13 }}>
              <strong>Batterie-Empfehlung:</strong>{" "}
              {selfConsResult.batteryRecommendationKwh} kWh —{" "}
              Autarkiegrad steigt auf{" "}
              {Math.round(selfConsResult.withBatteryAutarkyRate * 100)} %
              {" "}(Eigenverbrauch: {Math.round(selfConsResult.withBatterySelfConsumptionRate * 100)} %)
            </div>

            {/* Optimale Anlagengröße */}
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: 12, fontSize: 13, color: "#166534" }}>
              <strong>Empfehlung:</strong> {selfConsResult.optimalSystemNote}
            </div>
          </div>
        )}

        {!selfConsResult && !selfConsLoading && !selfConsError && (
          <div style={{ textAlign: "center", padding: 24, color: "var(--color-text-muted)", fontSize: 13 }}>
            Noch keine Berechnung. Jahresverbrauch eingeben und berechnen.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PVGIS Panel ──────────────────────────────────────────────────────────────

const PVGIS_MONTH_LABELS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

function azimuthLabel(deg: number): string {
  if (deg >= 337.5 || deg < 22.5) return "N";
  if (deg < 67.5) return "NO";
  if (deg < 112.5) return "O";
  if (deg < 157.5) return "SO";
  if (deg < 202.5) return "S";
  if (deg < 247.5) return "SW";
  if (deg < 292.5) return "W";
  return "NW";
}

function PvgisPanel({
  coords,
  pvgisData,
  pvgisLoading,
  onFetch,
}: {
  coords: { lat: number; lng: number } | null;
  pvgisData: PvgisResult | null;
  pvgisLoading: boolean;
  onFetch: (lat: number, lng: number) => Promise<PvgisResult | null>;
}) {
  const maxMonth =
    pvgisData && pvgisData.monthly.length > 0
      ? Math.max(...pvgisData.monthly.map((m) => m.emonth), 1)
      : 1;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600 }}>Sonneneinstrahlung (PVGIS)</h3>
        {pvgisData?.source === "pvgis" && (
          <span style={{
            fontSize: 11, fontWeight: 600, color: "#16a34a",
            background: "#f0fdf4", border: "1px solid #86efac",
            borderRadius: 4, padding: "2px 8px",
          }}>
            PVGIS-SARAH2
          </span>
        )}
        {pvgisData?.source === "unavailable" && (
          <span style={{
            fontSize: 11, fontWeight: 600, color: "#6b7280",
            background: "#f9fafb", border: "1px solid #d1d5db",
            borderRadius: 4, padding: "2px 8px",
          }}>
            Fallback-Werte
          </span>
        )}
      </div>

      {!coords && (
        <p style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
          Kein Gebäude mit Koordinaten gefunden. Bitte zuerst ein Gebäude im Tab "Gebäude" anlegen.
        </p>
      )}

      {coords && (
        <>
          <p style={{ fontSize: 13, color: "var(--color-text-muted)", marginBottom: 12 }}>
            Standort: {coords.lat.toFixed(4)}°N, {coords.lng.toFixed(4)}°E
          </p>
          <button
            className="btn-secondary"
            onClick={() => void onFetch(coords.lat, coords.lng)}
            disabled={pvgisLoading}
            style={{ fontSize: 13, marginBottom: 12 }}
          >
            {pvgisLoading ? "Lade Daten…" : "Sonneneinstrahlung abrufen"}
          </button>
        </>
      )}

      {pvgisData?.source === "unavailable" && (
        <div style={{
          padding: 16, borderRadius: 8, background: "#fff7ed",
          border: "1px solid #fdba74", color: "#9a3412", fontSize: 13,
        }}>
          <strong>PVGIS nicht erreichbar.</strong>{" "}
          {pvgisData.warning ?? "Es liegen derzeit keine Standort-Ertragsdaten vor."}{" "}
          Es werden bewusst <strong>keine</strong> Ertragswerte angezeigt. Für eine belastbare
          Berechnung bitte später erneut abrufen oder den TMY-Schnellmodus der Simulation nutzen.
        </div>
      )}

      {pvgisData && pvgisData.source === "pvgis" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <KpiCard
              label="Jahresertrag"
              value={`${pvgisData.annualYieldKwh.toLocaleString("de-AT")} kWh/kWp/a`}
            />
            <KpiCard
              label="Optimale Neigung"
              value={`${pvgisData.optimalTiltDeg}°`}
            />
            <KpiCard
              label="Optimale Ausrichtung"
              value={`${pvgisData.optimalAzimuthDeg}° (${azimuthLabel(pvgisData.optimalAzimuthDeg)})`}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <KpiCard
              label="Jahres-Irradianz"
              value={`${pvgisData.annualIrradianceKwhM2.toLocaleString("de-AT")} kWh/m²/a`}
            />
            <KpiCard
              label="Systemverluste"
              value={`${pvgisData.systemLossPercent} %`}
            />
          </div>

          {pvgisData.monthly.length > 0 && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Monatlicher Ertrag (kWh/Monat)</div>
              <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 80, marginTop: 8 }}>
                {pvgisData.monthly.map((m, i) => (
                  <div
                    key={i}
                    style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
                    title={`${PVGIS_MONTH_LABELS[i]}: ${m.emonth.toFixed(1)} kWh`}
                  >
                    <div style={{
                      height: `${Math.round((m.emonth / maxMonth) * 70)}px`,
                      background: m.emonth / maxMonth > 0.9 ? "#f59e0b" : "#2563eb",
                      borderRadius: "2px 2px 0 0",
                      width: "100%",
                      minHeight: 2,
                    }} />
                    <span style={{ fontSize: 9, color: "#6b7280" }}>{PVGIS_MONTH_LABELS[i]}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 12, fontSize: 11, color: "#6b7280" }}>
            Datenquelle: {pvgisData.raddatabase} (EU Joint Research Centre, PVGIS)
          </div>
          <div style={{ marginTop: 4, fontSize: 11, color: "#15803d" }}>
            Standortbezogene PVGIS-Referenz — genauer als die TMY-Vorbemessung der Simulation.
          </div>
        </div>
      )}

      {!pvgisData && !pvgisLoading && (
        <div style={{ textAlign: "center", padding: 24, color: "var(--color-text-muted)", fontSize: 13 }}>
          Noch keine PVGIS-Daten. Klicken Sie "Sonneneinstrahlung abrufen" um echte Standortdaten zu laden.
        </div>
      )}
    </div>
  );
}

// ─── Wirtschaftlichkeit ───────────────────────────────────────────────────────

interface EconFormState {
  systemSizeKwp: number;
  investmentEur: number;
  annualConsumptionKwh: number;
  electricityPriceCentsPerKwh: number;
  feedInTariffCentsPerKwh: number;
  analysisYears: number;
}

interface EconEngineData {
  paybackYears: number;
  annualSavingsEurExpected: number;
  annualSavingsEurMin: number;
  annualSavingsEurMax: number;
  npvEur: number;
  roi: number;
  irrPct?: number | null;
  cashflowSeries?: { year: number; cumulativeEur: number }[];
  co2SavedKgPerYear: number;
}

interface EconEngineResult {
  status: string;
  data: EconEngineData;
  confidenceScore: number;
  assumptions?: Array<{ field: string; assumed: unknown; reason: string }>;
}

interface EconApiResponse {
  analysis: unknown;
  engineResult: EconEngineResult;
}

function EconomicsTab({ projectId, revisionId }: { projectId: string; revisionId: string }) {
  const [form, setForm] = useState<EconFormState>({
    systemSizeKwp: 10,
    investmentEur: 15000,
    annualConsumptionKwh: 4500,
    electricityPriceCentsPerKwh: 28.5,
    feedInTariffCentsPerKwh: 8.24,
    analysisYears: 25,
  });
  const [result, setResult] = useState<EconEngineResult | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefillSource, setPrefillSource] = useState<string | null>(null);

  // Prefill aus echten Projektdaten: Dach-kWp (Dachentwurf) + Investition/Anlage
  // aus der letzten Wirtschaftlichkeitsanalyse — statt generischer Demo-Defaults.
  // Überschreibt nur Felder, die noch auf dem Default stehen (keine User-Eingaben).
  useEffect(() => {
    let kwpFromRoof: number | null = null;
    void apiClient
      .get<Array<{ summaryJson?: { result?: { estimatedKwp?: number } } }>>(`/projects/${projectId}/roof-designs`)
      .then((r) => {
        if (r.success && Array.isArray(r.data) && r.data.length > 0) {
          const k = r.data[0]?.summaryJson?.result?.estimatedKwp;
          if (typeof k === "number" && k > 0) {
            kwpFromRoof = k;
            setForm((f) => (f.systemSizeKwp === 10 ? { ...f, systemSizeKwp: k } : f));
            setPrefillSource("Dachentwurf");
          }
        }
      });
    void apiClient
      .get<Array<{ systemSizeKwp?: number; investmentEur?: number }>>(`/projects/${projectId}/economics`)
      .then((r) => {
        if (!r.success || !Array.isArray(r.data) || r.data.length === 0) return;
        const a = r.data[0];
        setForm((f) => {
          const next = { ...f };
          if (kwpFromRoof == null && typeof a?.systemSizeKwp === "number" && a.systemSizeKwp > 0 && f.systemSizeKwp === 10) {
            next.systemSizeKwp = a.systemSizeKwp;
          }
          if (typeof a?.investmentEur === "number" && a.investmentEur > 0 && f.investmentEur === 15000) {
            next.investmentEur = a.investmentEur;
          }
          return next;
        });
        setPrefillSource((prev) => prev ?? "letzte Analyse");
      });
    // Investition aus der berechneten Kalkulation übernehmen (höhere Priorität
    // als der Demo-Default; nur solange der Nutzer nichts geändert hat).
    void apiClient
      .get<{ items: Array<{ totalNetEur?: number }> }>(`/projects/${projectId}/costing/calculations`)
      .then((r) => {
        if (!r.success) return;
        const net = r.data?.items?.[0]?.totalNetEur;
        if (typeof net === "number" && net > 0) {
          setForm((f) => (f.investmentEur === 15000 ? { ...f, investmentEur: net } : f));
          setPrefillSource((prev) => prev ?? "Kalkulation");
        }
      });
  }, [projectId]);

  const handleStart = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    const res = await apiClient.post<EconApiResponse>(`/projects/${projectId}/economics`, {
      revisionId,
      ...form,
    });
    if (res.success && res.data?.engineResult) {
      setResult(res.data.engineResult);
    } else {
      setError("Berechnung fehlgeschlagen. Bitte erneut versuchen.");
    }
    setRunning(false);
  };

  const engineData = result?.data;

  // Eigenverbrauchsquote: der Wert, den die Engine tatsächlich für die
  // Einsparung/NPV verwendet hat (assumptions[selfConsumptionRatio], Dezimal
  // 0..0.95) — nicht eine abweichende, von der Engine entkoppelte UI-Heuristik.
  const selfConsAssumption = result?.assumptions?.find((a) => a.field === "selfConsumptionRatio");
  const selfConsRatioPct =
    typeof selfConsAssumption?.assumed === "number" ? Math.round(selfConsAssumption.assumed * 100) : null;

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Wirtschaftlichkeit</h2>
      </div>

      {/* Formular */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600 }}>Berechnungsparameter</h3>
          {prefillSource && (
            <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
              Anlage/Investition vorbefüllt aus {prefillSource} — manuell überschreibbar
            </span>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <FormField label="Anlagengröße (kWp)">
            <input type="number" min="0.5" step="0.5" value={form.systemSizeKwp}
              onChange={(e) => setForm((f) => ({ ...f, systemSizeKwp: parseFloat(e.target.value) }))}
              style={inputStyle} />
          </FormField>
          <FormField label="Investitionskosten (€)">
            <input type="number" min="0" step="100" value={form.investmentEur}
              onChange={(e) => setForm((f) => ({ ...f, investmentEur: parseFloat(e.target.value) }))}
              style={inputStyle} />
          </FormField>
          <FormField label="Jahresverbrauch (kWh)">
            <input type="number" min="0" step="100" value={form.annualConsumptionKwh}
              onChange={(e) => setForm((f) => ({ ...f, annualConsumptionKwh: parseFloat(e.target.value) }))}
              style={inputStyle} />
          </FormField>
          <FormField label="Strompreis (Cent/kWh)">
            <input type="number" min="0" step="0.1" value={form.electricityPriceCentsPerKwh}
              onChange={(e) => setForm((f) => ({ ...f, electricityPriceCentsPerKwh: parseFloat(e.target.value) }))}
              style={inputStyle} />
          </FormField>
          <FormField label="Einspeisevergütung (Cent/kWh)">
            <input type="number" min="0" step="0.01" value={form.feedInTariffCentsPerKwh}
              onChange={(e) => setForm((f) => ({ ...f, feedInTariffCentsPerKwh: parseFloat(e.target.value) }))}
              style={inputStyle} />
          </FormField>
          <FormField label="Analysezeitraum (Jahre)">
            <input type="number" min="1" max="40" value={form.analysisYears}
              onChange={(e) => setForm((f) => ({ ...f, analysisYears: parseInt(e.target.value, 10) }))}
              style={inputStyle} />
          </FormField>
        </div>
        <div style={{ marginTop: 16 }}>
          <button className="btn-primary" onClick={() => void handleStart()} disabled={running || !revisionId}>
            {running ? "Berechnung läuft…" : "Berechnung starten"}
          </button>
          {!revisionId && (
            <span style={{ marginLeft: 12, fontSize: 12, color: "var(--color-error)" }}>
              Keine Revision vorhanden
            </span>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, color: "#dc2626" }}>
          {error}
        </div>
      )}

      {/* Ergebnisse */}
      {engineData && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <KpiCard
              label="Amortisation"
              value={`${num(engineData.paybackYears, 1)} Jahre`}
            />
            <KpiCard
              label="Jährl. Einsparung"
              value={`${eur(engineData.annualSavingsEurExpected)}/J`}
              sub={`Bandbreite: ${num(engineData.annualSavingsEurMin)} – ${eur(engineData.annualSavingsEurMax)}`}
            />
            <KpiCard
              label={`NPV (${form.analysisYears} Jahre)`}
              value={eur(engineData.npvEur)}
              tone={engineData.npvEur < 0 ? "negative" : undefined}
            />
            <KpiCard
              label="ROI"
              value={pct(engineData.roi, 1)}
            />
            <KpiCard
              label="IRR (interner Zinsfuß)"
              value={engineData.irrPct != null ? pct(engineData.irrPct, 1) : "—"}
              sub="über die Laufzeit"
            />
            <KpiCard
              label="CO₂-Einsparung"
              value={`${num(engineData.co2SavedKgPerYear)} kg/Jahr`}
            />
            <KpiCard
              label="Eigenverbrauch"
              value={selfConsRatioPct != null ? pct(selfConsRatioPct, 0) : "—"}
              sub="Engine-Annahme (Heuristik)"
            />
          </div>

          {engineData.cashflowSeries && engineData.cashflowSeries.length > 1 && (() => {
            const s = engineData.cashflowSeries;
            const W = 640, H = 200, padL = 60, padR = 16, padT = 14, padB = 26;
            const vals = s.map((p) => p.cumulativeEur);
            const minY = Math.min(0, ...vals), maxY = Math.max(0, ...vals);
            const maxX = Math.max(...s.map((p) => p.year), 1);
            const last = vals[vals.length - 1] ?? 0;
            const x = (yr: number) => padL + (yr / maxX) * (W - padL - padR);
            const y = (v: number) => padT + (1 - (v - minY) / (maxY - minY || 1)) * (H - padT - padB);
            const line = s.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.year).toFixed(1)},${y(p.cumulativeEur).toFixed(1)}`).join(" ");
            const zeroY = y(0);
            const be = s.find((p) => p.cumulativeEur >= 0);
            return (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Kumulierter Cashflow (Ersparnis − Investition, ohne Förderung)</div>
                <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
                  <line x1={padL} y1={zeroY} x2={W - padR} y2={zeroY} stroke="#94a3b8" strokeDasharray="4 3" />
                  <text x={padL - 6} y={zeroY + 3} textAnchor="end" fontSize="9" fill="#64748b">0 €</text>
                  <path d={`${line} L${x(maxX).toFixed(1)},${zeroY.toFixed(1)} L${x(0).toFixed(1)},${zeroY.toFixed(1)} Z`} fill="#2563eb" fillOpacity={0.08} />
                  <path d={line} fill="none" stroke="#2563eb" strokeWidth={2} />
                  {be && (
                    <>
                      <line x1={x(be.year)} y1={padT} x2={x(be.year)} y2={H - padB} stroke="#16a34a" strokeDasharray="3 3" />
                      <text x={x(be.year)} y={padT + 8} textAnchor="middle" fontSize="9" fill="#15803d">Break-even ~J{be.year}</text>
                    </>
                  )}
                  {[0, Math.round(maxX / 2), maxX].map((yr) => (
                    <text key={yr} x={x(yr)} y={H - padB + 14} textAnchor="middle" fontSize="9" fill="#64748b">J{yr}</text>
                  ))}
                  <text x={W - padR} y={y(last) - 4} textAnchor="end" fontSize="9" fill="#1e3a8a">{Math.round(last).toLocaleString("de-AT")} €</text>
                </svg>
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4 }}>
                  Undiskontiert, ohne Förderung; Förderung verkürzt die Amortisation zusätzlich. Richtwerte, keine Zusage.
                </div>
              </div>
            );
          })()}

          {result.confidenceScore != null && (
            <div className="card">
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                <span style={{ fontWeight: 500 }}>Konfidenz-Score</span>
                <span style={{ color: "var(--color-text-muted)" }}>{pct(result.confidenceScore * 100, 0)}</span>
              </div>
              <div style={{ background: "var(--color-border)", borderRadius: 4, height: 8, overflow: "hidden" }}>
                <div
                  style={{
                    width: `${result.confidenceScore * 100}%`,
                    height: "100%",
                    backgroundColor: result.confidenceScore >= 0.7 ? "var(--color-success)" : result.confidenceScore >= 0.4 ? "var(--color-warning)" : "var(--color-error)",
                    borderRadius: 4,
                    transition: "width 0.4s",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {!engineData && !running && !error && (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>
          Noch keine Berechnung. Starten Sie die Wirtschaftlichkeitsanalyse, um Ergebnisse zu sehen.
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* Speicher-Zusatzbewertung B5 (Stage 2 — tiefes Arbeitsblatt)        */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div style={{ marginTop: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
          Speicher-Zusatzbewertung (B5)
        </h3>
        <StorageB5WorksheetPanel
          initialValues={{
            strompreisCtProKwh: form.electricityPriceCentsPerKwh,
            einspeisetarifCtProKwh: form.feedInTariffCentsPerKwh,
            pvSystemKwp: form.systemSizeKwp,
            existingPaybackYears: result?.data?.paybackYears ?? null,
          }}
          variant="card"
        />
      </div>
    </div>
  );
}

// ─── Elektroplanung ───────────────────────────────────────────────────────────

interface EngFormState {
  systemSizeKwp: number;
  modulePeakPowerWp: number;
}

interface EngEngineData {
  modulesTotal: number;
  modulesPerString: number;
  numberOfStrings: number;
  dcCurrentA: number;
  recommendedCableMm2: number;
  inverterSizeKw: number;
}

interface EngApiResponse {
  assessment: unknown;
  engineResult: {
    data: EngEngineData;
  };
}

function EngineeringTab({ projectId, revisionId }: { projectId: string; revisionId: string }) {
  const [form, setForm] = useState<EngFormState>({
    systemSizeKwp: 10,
    modulePeakPowerWp: 440,
  });
  const [result, setResult] = useState<EngEngineData | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStart = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    const res = await apiClient.post<EngApiResponse>(`/projects/${projectId}/engineering`, {
      revisionId,
      ...form,
    });
    if (res.success && res.data?.engineResult?.data) {
      setResult(res.data.engineResult.data);
    } else {
      setError("Auslegung fehlgeschlagen. Bitte erneut versuchen.");
    }
    setRunning(false);
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Elektroplanung</h2>
      </div>

      {/* Intro note */}
      <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 12, padding: "8px 12px", background: "#f9fafb", borderRadius: 6 }}>
        Grobe Auslegungsberechnung (Vorplanung). Modul-String-Details im Stringing-Check unten.
        Endauslegung durch qualifizierten Elektriker.
      </div>

      {/* Formular */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Auslegungsparameter</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FormField label="Anlagengröße (kWp)">
            <input type="number" min="0.5" step="0.5" value={form.systemSizeKwp}
              onChange={(e) => setForm((f) => ({ ...f, systemSizeKwp: parseFloat(e.target.value) }))}
              style={inputStyle} />
          </FormField>
          <FormField label="Modul-Spitzenleistung (Wp)">
            <input type="number" min="100" step="5" value={form.modulePeakPowerWp}
              onChange={(e) => setForm((f) => ({ ...f, modulePeakPowerWp: parseInt(e.target.value, 10) }))}
              style={inputStyle} />
          </FormField>
        </div>
        <div style={{ marginTop: 16 }}>
          <button className="btn-primary" onClick={() => void handleStart()} disabled={running || !revisionId}>
            {running ? "Berechnung läuft…" : "Auslegung berechnen"}
          </button>
          {!revisionId && (
            <span style={{ marginLeft: 12, fontSize: 12, color: "var(--color-error)" }}>
              Keine Revision vorhanden
            </span>
          )}
        </div>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, color: "#dc2626" }}>
          {error}
        </div>
      )}

      {/* Ergebnisse */}
      {result && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <KpiCard label="Modulanzahl" value={`${result.modulesTotal} Module`} />
            <KpiCard label="Module/String" value={`${result.modulesPerString}`} />
            <KpiCard label="Anzahl Strings" value={`${result.numberOfStrings}`} />
            <KpiCard label="DC-Strom" value={`${num(result.dcCurrentA, 2)} A`} />
            <KpiCard label="Kabelquerschnitt" value={`${result.recommendedCableMm2} mm²`} />
            <KpiCard label="WR-Leistung" value={`${num(result.inverterSizeKw, 2)} kW`} />
          </div>
          <div className="card" style={{ background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12, color: "#92400e", padding: "10px 14px", marginTop: 12 }}>
            Vorlaeufige Auslegung - kein Installationsplan, keine TOR-Freigabe, keine Netz-Zustimmung.
          </div>
        </div>
      )}

      {!result && !running && !error && (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>
          Noch keine Auslegung. Starten Sie die Berechnung, um Ergebnisse zu sehen.
        </div>
      )}

      {/* Stringing-Check */}
      <div style={{ borderTop: "2px solid var(--color-border)", marginTop: 32, paddingTop: 24 }}>
        <InverterStringPanel projectId={projectId} />
      </div>
    </div>
  );
}

// ─── Inverter / Stringing Panel ───────────────────────────────────────────────

interface InvStrFormState {
  moduleVocV: string;
  modulePowerWp: string;
  moduleTempCoeffVoc: string;
  modulesPerString: string;
  stringCount: string;
  inverterMaxInputVoltageV: string;
  inverterMpptVoltageMinV: string;
  inverterMpptVoltageMaxV: string;
  inverterMaxStringInputs: string;
  inverterNominalPowerKw: string;
  siteMinTempC: string;
}

interface InvStrData {
  vocEstimateV: number;
  mpptVoltageEstimateV: number;
  vocWithinLimit: boolean;
  mpptVoltageInRange: boolean;
  stringCountValid: boolean | null;
  totalPowerWp: number;
  dcAcRatio: number;
  dcAcRatioWarning: boolean;
  draftCheckPossible: boolean;
  requiresElectricalReview: true;
}

interface InvStrApiResponse {
  result: {
    data: {
      verdict: string;
      summary: string;
      detail: InvStrData;
      requiresHumanReview: true;
      canApprove: false;
    };
    warnings: Array<{ code: string; message: string; severity: string }>;
    assumptions: Array<{ field: string; assumed: string; reason: string }>;
    confidenceScore: number;
  };
  canApprove: false;
  isDraft: true;
  requiresElectricalReview: true;
}

const REQUIRED_INV_FIELDS: (keyof InvStrFormState)[] = [
  "moduleVocV", "modulePowerWp", "modulesPerString", "stringCount",
  "inverterMaxInputVoltageV", "inverterMpptVoltageMinV",
  "inverterMpptVoltageMaxV", "inverterNominalPowerKw",
];

const REQUIRED_INV_LABELS: Record<string, string> = {
  moduleVocV: "Modul-Leerlaufspannung Voc (V)",
  modulePowerWp: "Modul-Nennleistung (Wp)",
  modulesPerString: "Module pro String",
  stringCount: "Anzahl Strings",
  inverterMaxInputVoltageV: "Max. DC-Eingangsspannung WR (V)",
  inverterMpptVoltageMinV: "MPPT-Mindestspannung (V)",
  inverterMpptVoltageMaxV: "MPPT-Maximalspannung (V)",
  inverterNominalPowerKw: "Wechselrichter-Nennleistung (kW)",
};

function parsePosNum(s: string): number | undefined {
  const n = parseFloat(s);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function parsePosInt(s: string): number | undefined {
  const n = parseInt(s, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

// ─── Komponenten-Katalog: Typen für Block C (auto-fill) ──────────────────────

interface CatalogEntry {
  id: string;
  type: string;
  manufacturer: string;
  modelName: string;
  dataQuality: string;
  isVerified: boolean;
  specs: Record<string, unknown>;
}

/** Liest einen spec-Wert als positive Zahl oder undefined. */
function specNum(specs: Record<string, unknown>, key: string): number | undefined {
  const v = specs[key];
  return typeof v === "number" && v > 0 ? v : undefined;
}

function InverterStringPanel({ projectId }: { projectId: string }) {
  const [form, setForm] = useState<InvStrFormState>({
    moduleVocV: "",
    modulePowerWp: "",
    moduleTempCoeffVoc: "-0.35",
    modulesPerString: "",
    stringCount: "",
    inverterMaxInputVoltageV: "1000",
    inverterMpptVoltageMinV: "",
    inverterMpptVoltageMaxV: "",
    inverterMaxStringInputs: "",
    inverterNominalPowerKw: "",
    siteMinTempC: "-10",
  });
  const [checkResult, setCheckResult] = useState<InvStrApiResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Block C: Komponenten-Katalog (auto-fill) ───────────────────────────────
  const [catalogInverters, setCatalogInverters] = useState<CatalogEntry[]>([]);
  const [catalogModules, setCatalogModules] = useState<CatalogEntry[]>([]);
  const [selectedInverterId, setSelectedInverterId] = useState<string>("");
  const [selectedModuleId, setSelectedModuleId] = useState<string>("");
  const [catalogLoaded, setCatalogLoaded] = useState(false);

  // Lade Katalog beim Mount
  useEffect(() => {
    let cancelled = false;

    async function loadCatalog() {
      const [invRes, modRes] = await Promise.all([
        apiClient.get<CatalogEntry[]>(`/components?type=inverter`),
        apiClient.get<CatalogEntry[]>(`/components?type=pv_module`),
      ]);
      if (cancelled) return;
      if (invRes.success && Array.isArray(invRes.data)) {
        setCatalogInverters(invRes.data);
      }
      if (modRes.success && Array.isArray(modRes.data)) {
        setCatalogModules(modRes.data);
      }
      setCatalogLoaded(true);
    }

    void loadCatalog();
    return () => { cancelled = true; };
  }, []); // Einmaliger Load — kein projectId-dep nötig

  // Aus der Modulbelegung (Planung) vorbefüllen: die Belegung berechnet bereits
  // Voc/Wp/Module-pro-String/Strings — der Nutzer soll dieselben Werte nicht
  // erneut tippen. Nur LEERE Felder setzen (keine manuelle/Katalog-Eingabe
  // überschreiben). Neueste Belegung = layoutData[0] (createdAt desc).
  useEffect(() => {
    let cancelled = false;
    async function prefillFromPlanning() {
      const res = await apiClient.get<Array<{ layoutData?: Record<string, unknown> }>>(
        `/projects/${projectId}/planning`,
      );
      if (cancelled || !res.success || !Array.isArray(res.data) || res.data.length === 0) return;
      const ld = res.data[0]?.layoutData;
      if (!ld) return;
      const voc = ld.vocPerModule, wp = ld.modulePeakWp, mps = ld.modulesPerString, ns = ld.numberOfStrings;
      setForm((prev) => ({
        ...prev,
        ...(prev.moduleVocV === "" && typeof voc === "number" && voc > 0 ? { moduleVocV: String(voc) } : {}),
        ...(prev.modulePowerWp === "" && typeof wp === "number" && wp > 0 ? { modulePowerWp: String(wp) } : {}),
        ...(prev.modulesPerString === "" && typeof mps === "number" && mps > 0 ? { modulesPerString: String(mps) } : {}),
        ...(prev.stringCount === "" && typeof ns === "number" && ns > 0 ? { stringCount: String(ns) } : {}),
      }));
    }
    void prefillFromPlanning();
    return () => { cancelled = true; };
  }, [projectId]);

  // Wechselrichter aus Katalog wählen → Formular auto-füllen
  function handleSelectInverter(id: string) {
    setSelectedInverterId(id);
    if (!id) return;
    const inv = catalogInverters.find((c) => c.id === id);
    if (!inv) return;
    const s = inv.specs;
    setForm((prev) => ({
      ...prev,
      ...(specNum(s, "maxInputVoltageV") != null ? { inverterMaxInputVoltageV: String(specNum(s, "maxInputVoltageV")) } : {}),
      ...(specNum(s, "mpptVoltageMinV") != null ? { inverterMpptVoltageMinV: String(specNum(s, "mpptVoltageMinV")) } : {}),
      ...(specNum(s, "mpptVoltageMaxV") != null ? { inverterMpptVoltageMaxV: String(specNum(s, "mpptVoltageMaxV")) } : {}),
      ...(specNum(s, "nominalPowerKw") != null ? { inverterNominalPowerKw: String(specNum(s, "nominalPowerKw")) } : {}),
      ...(specNum(s, "maxStringInputs") != null ? { inverterMaxStringInputs: String(specNum(s, "maxStringInputs")) } : {}),
    }));
  }

  // PV-Modul aus Katalog wählen → Formular auto-füllen
  function handleSelectModule(id: string) {
    setSelectedModuleId(id);
    if (!id) return;
    const mod = catalogModules.find((c) => c.id === id);
    if (!mod) return;
    const s = mod.specs;
    setForm((prev) => ({
      ...prev,
      ...(specNum(s, "vocV") != null ? { moduleVocV: String(specNum(s, "vocV")) } : {}),
      ...(specNum(s, "nominalPowerWp") != null ? { modulePowerWp: String(specNum(s, "nominalPowerWp")) } : {}),
      // tempCoeffVoc: negative Zahl, separate Behandlung
      ...(typeof s.tempCoeffVoc === "number" && s.tempCoeffVoc < 0
        ? { moduleTempCoeffVoc: String(s.tempCoeffVoc) }
        : {}),
    }));
  }

  // Datenqualitäts-Warnung: true wenn gewählte Komponente sample/unverified
  const selectedInvEntry = catalogInverters.find((c) => c.id === selectedInverterId);
  const selectedModEntry = catalogModules.find((c) => c.id === selectedModuleId);
  const hasUnverifiedData =
    (selectedInvEntry != null && (selectedInvEntry.dataQuality !== "verified" || !selectedInvEntry.isVerified)) ||
    (selectedModEntry != null && (selectedModEntry.dataQuality !== "verified" || !selectedModEntry.isVerified));

  const missingRequired = REQUIRED_INV_FIELDS.filter(
    (f) => !form[f] || parseFloat(form[f]) <= 0,
  );

  const f = (key: keyof InvStrFormState) => form[key];
  const set = (key: keyof InvStrFormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    const body: Record<string, number> = {
      moduleVocV:               parsePosNum(f("moduleVocV")) ?? 0,
      modulePowerWp:            parsePosNum(f("modulePowerWp")) ?? 0,
      modulesPerString:         parsePosInt(f("modulesPerString")) ?? 0,
      stringCount:              parsePosInt(f("stringCount")) ?? 0,
      inverterMaxInputVoltageV: parsePosNum(f("inverterMaxInputVoltageV")) ?? 0,
      inverterMpptVoltageMinV:  parsePosNum(f("inverterMpptVoltageMinV")) ?? 0,
      inverterMpptVoltageMaxV:  parsePosNum(f("inverterMpptVoltageMaxV")) ?? 0,
      inverterNominalPowerKw:   parsePosNum(f("inverterNominalPowerKw")) ?? 0,
    };
    const tempCoeff = parseFloat(f("moduleTempCoeffVoc"));
    if (Number.isFinite(tempCoeff) && tempCoeff < 0) body.moduleTempCoeffVoc = tempCoeff;
    const siteMin = parseFloat(f("siteMinTempC"));
    if (Number.isFinite(siteMin)) body.siteMinTempC = siteMin;
    const maxStr = parsePosInt(f("inverterMaxStringInputs"));
    if (maxStr !== undefined) body.inverterMaxStringInputs = maxStr;

    const res = await apiClient.post<InvStrApiResponse>(
      `/projects/${projectId}/engineering/stringing-check`,
      body,
    );
    if (res.success) {
      setCheckResult(res.data);
    } else {
      setError("Prüfung fehlgeschlagen — bitte alle Pflichtfelder prüfen.");
    }
    setRunning(false);
  };

  const r = checkResult?.result;
  const d = r?.data?.detail;

  const statusIcon = (ok: boolean | null, warnOnFail = false) => {
    if (ok === null) return <span style={{ color: "#9ca3af" }}>–</span>;
    if (ok) return <span style={{ color: "#16a34a", fontWeight: 700 }}>✓ OK</span>;
    return <span style={{ color: warnOnFail ? "#d97706" : "#dc2626", fontWeight: 700 }}>
      {warnOnFail ? "⚠ Prüfen" : "✗ Fehler"}
    </span>;
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>⚡ Wechselrichter / Stringing-Prüfung</h3>
        <span style={{
          background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d",
          borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 700,
        }}>ENTWURF</span>
      </div>

      {/* Safety banner */}
      <div style={{
        background: "#fef9c3", border: "1px solid #fde047", borderRadius: 6,
        padding: "10px 14px", marginBottom: 16, fontSize: 12,
      }}>
        <strong>⚡ Elektrische Auslegung ist ein Entwurf.</strong>{" "}
        Endprüfung durch qualifizierten Elektriker zwingend erforderlich.
        Keine TOR-/Netz-Freigabe. Nicht genehmigt. Keine Freigabe durch KI.
      </div>

      {/* ── Block C: Komponenten-Katalog-Selektor ─────────────────────────────── */}
      {catalogLoaded && (catalogInverters.length > 0 || catalogModules.length > 0) && (
        <div className="card" style={{ marginBottom: 12, padding: "10px 14px" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
            🔧 Komponente aus Katalog wählen (auto-fill)
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {/* Wechselrichter-Selektor */}
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3 }}>
                Wechselrichter
              </label>
              <select
                value={selectedInverterId}
                onChange={(e) => handleSelectInverter(e.target.value)}
                style={{ width: "100%", padding: "5px 8px", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 12, background: "#fff" }}
              >
                <option value="">— Manuell eingeben —</option>
                {catalogInverters.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.manufacturer} {c.modelName}
                    {c.dataQuality === "sample" ? " [MUSTER]" : c.dataQuality === "unverified" ? " [ungeprüft]" : ""}
                  </option>
                ))}
              </select>
              {selectedInvEntry && (
                <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>
                  {selectedInvEntry.dataQuality === "sample"
                    ? "⚠ SAMPLE-Daten — nur für Demo"
                    : !selectedInvEntry.isVerified
                      ? "⚠ Nicht verifiziert — Datenblatt prüfen"
                      : "✓ Verifiziert"}
                </div>
              )}
            </div>
            {/* PV-Modul-Selektor */}
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3 }}>
                PV-Modul
              </label>
              <select
                value={selectedModuleId}
                onChange={(e) => handleSelectModule(e.target.value)}
                style={{ width: "100%", padding: "5px 8px", border: "1px solid #d1d5db", borderRadius: 4, fontSize: 12, background: "#fff" }}
              >
                <option value="">— Manuell eingeben —</option>
                {catalogModules.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.manufacturer} {c.modelName}
                    {c.dataQuality === "sample" ? " [MUSTER]" : c.dataQuality === "unverified" ? " [ungeprüft]" : ""}
                  </option>
                ))}
              </select>
              {selectedModEntry && (
                <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>
                  {selectedModEntry.dataQuality === "sample"
                    ? "⚠ SAMPLE-Daten — nur für Demo"
                    : !selectedModEntry.isVerified
                      ? "⚠ Nicht verifiziert — Datenblatt prüfen"
                      : "✓ Verifiziert"}
                </div>
              )}
            </div>
          </div>
          {hasUnverifiedData && (
            <div style={{
              marginTop: 8, padding: "6px 10px",
              background: "#fffbeb", border: "1px solid #fcd34d",
              borderRadius: 4, fontSize: 11, color: "#92400e",
            }}>
              ⚠ <strong>MUSTER/NICHT VERIFIZIERT:</strong> Die gewählten Katalog-Daten sind Musterwerte.
              Vor Inbetriebnahme mit dem Original-Datenblatt des Herstellers abgleichen.
              Manuelle Überschreibung der Felder ist jederzeit möglich.
            </div>
          )}
        </div>
      )}
      {catalogLoaded && catalogInverters.length === 0 && catalogModules.length === 0 && (
        <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 10 }}>
          Kein Komponentenkatalog verfügbar — manuelle Eingabe erforderlich.
        </div>
      )}

      {/* Input form */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Eingabedaten</h4>

        {/* Module params */}
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
          PV-Modul
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          <FormField label="Voc bei STC (V) *">
            <input type="number" min="1" step="0.1" value={f("moduleVocV")} onChange={set("moduleVocV")} placeholder="z.B. 41.2" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>Datenblatt: Leerlaufspannung @ STC (25 degC, 1000 W/m2). Wird mit Kaeltekorrekturfaktor skaliert.</div>
          </FormField>
          <FormField label="Nennleistung (Wp) *">
            <input type="number" min="1" step="5" value={f("modulePowerWp")} onChange={set("modulePowerWp")} placeholder="z.B. 440" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>Datenblatt: Peakleistung @ STC.</div>
          </FormField>
          <FormField label="Temp.-Koeff. Voc (%/°C)">
            <input type="number" step="0.01" value={f("moduleTempCoeffVoc")} onChange={set("moduleTempCoeffVoc")} placeholder="-0.35" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>Negativer Koeff. Typisch -0,25 bis -0,45 %/degC. Datenblatt: gamma-Voc.</div>
          </FormField>
        </div>

        {/* String configuration */}
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
          String-Konfiguration
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          <FormField label="Module pro String *">
            <input type="number" min="1" step="1" value={f("modulesPerString")} onChange={set("modulesPerString")} placeholder="z.B. 14" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>Reihenschaltung: bestimmt String-Spannung. Typisch 14-20 Module.</div>
          </FormField>
          <FormField label="Anzahl Strings *">
            <input type="number" min="1" step="1" value={f("stringCount")} onChange={set("stringCount")} placeholder="z.B. 2" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>Parallelschaltung: bestimmt Gesamtstrom.</div>
          </FormField>
          <FormField label="Min. Standorttemp. (°C)">
            <input type="number" step="1" value={f("siteMinTempC")} onChange={set("siteMinTempC")} placeholder="-10" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>Kaelteste Temperatur am Standort. Bestimmt Worst-Case Kalt-Voc.</div>
          </FormField>
        </div>

        {/* Inverter params */}
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
          Wechselrichter
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          <FormField label="Max. DC-Eingang (V) *">
            <input type="number" min="1" step="10" value={f("inverterMaxInputVoltageV")} onChange={set("inverterMaxInputVoltageV")} placeholder="1000" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>Max. zul. DC-Eingangsspannung lt. WR-Datenblatt. Typisch 600-1500 V.</div>
          </FormField>
          <FormField label="MPPT-Min (V) *">
            <input type="number" min="1" step="1" value={f("inverterMpptVoltageMinV")} onChange={set("inverterMpptVoltageMinV")} placeholder="z.B. 200" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>Untere Grenze MPPT-Fenster. Vmpp muss darueber liegen.</div>
          </FormField>
          <FormField label="MPPT-Max (V) *">
            <input type="number" min="1" step="1" value={f("inverterMpptVoltageMaxV")} onChange={set("inverterMpptVoltageMaxV")} placeholder="z.B. 800" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>Obere Grenze MPPT-Fenster. Vmpp muss darunter liegen.</div>
          </FormField>
          <FormField label="Nennleistung WR (kW) *">
            <input type="number" min="0.1" step="0.1" value={f("inverterNominalPowerKw")} onChange={set("inverterNominalPowerKw")} placeholder="z.B. 10" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>AC-Nennleistung des WR. Bestimmt das DC/AC-Verhaeltnis.</div>
          </FormField>
          <FormField label="Max. String-Eingänge">
            <input type="number" min="1" step="1" value={f("inverterMaxStringInputs")} onChange={set("inverterMaxStringInputs")} placeholder="optional" style={inputStyle} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>Max. Anzahl String-Eingaenge lt. WR-Datenblatt. Leer = keine Pruefung.</div>
          </FormField>
        </div>

        {/* Missing required fields hint */}
        {missingRequired.length > 0 && (
          <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 4, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>
            <strong style={{ color: "#dc2626" }}>Fehlende Pflichtfelder:</strong>
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              {missingRequired.map((f) => (
                <li key={f} style={{ color: "#dc2626" }}>{REQUIRED_INV_LABELS[f] ?? f}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Typische Werte hint — show while any field is still empty */}
        {Object.values(form).some((v) => v === "") && (
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", background: "#f9fafb", borderRadius: 4, padding: "6px 10px", marginBottom: 8 }}>
            <strong>Typische Werte (Beispiel 400Wp Modul):</strong>{" "}
            Voc = 37-42 V &middot; Temp.-Koeff = -0,35 %/degC &middot; Module/String = 14-20 &middot; WR max. DC = 1000 V &middot; MPPT min = 200 V &middot; MPPT max = 850 V
          </div>
        )}

        <button
          className="btn-primary"
          onClick={() => void handleRun()}
          disabled={running || missingRequired.length > 0}
          style={{ fontSize: 13 }}
        >
          {running ? "Prüfe…" : "Stringing prüfen"}
        </button>
        <span style={{ marginLeft: 10, fontSize: 11, color: "var(--color-text-muted)" }}>
          * Pflichtfeld
        </span>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, color: "#dc2626" }}>
          {error}
        </div>
      )}

      {/* Results */}
      {r && d && (
        <div>
          {/* Summary verdict */}
          <div style={{
            borderRadius: 8, padding: "12px 16px", marginBottom: 16,
            background: r.data.verdict === "critical" ? "#fef2f2"
              : r.data.verdict === "warning" ? "#fffbeb"
              : "#f0fdf4",
            border: `1px solid ${r.data.verdict === "critical" ? "#fca5a5" : r.data.verdict === "warning" ? "#fde68a" : "#86efac"}`,
          }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {r.data.verdict === "critical" ? "❌" : r.data.verdict === "warning" ? "⚠️" : "✅"}{" "}
              {r.data.summary}
            </div>
            <div style={{ fontSize: 11, marginTop: 4, color: "#6b7280" }}>
              Konfidenz: {Math.round(r.confidenceScore * 100)}% · ENTWURF
            </div>
          </div>

          {/* KPI grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div className="card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: d.vocWithinLimit ? "#16a34a" : "#dc2626" }}>
                {d.vocEstimateV.toFixed(0)} V
              </div>
              <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4 }}>Kalt-Voc @ Mindesttemp. (Worst-Case)</div>
              <div style={{ marginTop: 4 }}>{statusIcon(d.vocWithinLimit, false)}</div>
            </div>
            <div className="card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: d.mpptVoltageInRange ? "#16a34a" : "#d97706" }}>
                {d.mpptVoltageEstimateV.toFixed(0)} V
              </div>
              <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4 }}>Vmpp-Schaetzung (MPP bei Betrieb)</div>
              <div style={{ marginTop: 4 }}>{statusIcon(d.mpptVoltageInRange, true)}</div>
            </div>
            <div className="card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: d.dcAcRatioWarning ? "#d97706" : "#16a34a" }}>
                {num(d.dcAcRatio, 2)}
              </div>
              <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4 }}>DC/AC-Verhaeltnis (ideal: 1.0-1.25)</div>
              <div style={{ marginTop: 4 }}>{statusIcon(!d.dcAcRatioWarning, true)}</div>
            </div>
            <div className="card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: "var(--color-primary)" }}>
                {(d.totalPowerWp / 1000).toFixed(2)} kWp
              </div>
              <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4 }}>DC-Gesamtleistung (String-Array)</div>
              {d.stringCountValid !== null && (
                <div style={{ marginTop: 4 }}>{statusIcon(d.stringCountValid, true)}</div>
              )}
            </div>
          </div>

          {/* Warnings */}
          {r.warnings.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Hinweise & Warnungen</h4>
              {r.warnings.map((w) => (
                <div key={w.code} style={{
                  display: "flex", gap: 8, padding: "6px 0",
                  borderBottom: "1px solid var(--color-border)", fontSize: 13,
                }}>
                  <span style={{
                    color: w.severity === "critical" ? "#dc2626" : w.severity === "warning" ? "#d97706" : "#2563eb",
                    minWidth: 16,
                  }}>
                    {w.severity === "critical" ? "❌" : w.severity === "warning" ? "⚠️" : "ℹ️"}
                  </span>
                  <span>{w.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Assumptions */}
          {r.assumptions.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Annahmen</h4>
              {r.assumptions.map((a) => (
                <div key={a.field} style={{
                  padding: "5px 0", borderBottom: "1px solid var(--color-border)",
                  fontSize: 12, color: "var(--color-text-muted)",
                }}>
                  <strong style={{ color: "var(--color-text)" }}>{a.assumed}</strong> — {a.reason}
                </div>
              ))}
            </div>
          )}

          {/* Safety section */}
          <div style={{
            background: "#fff7ed", border: "2px solid #fed7aa", borderRadius: 8,
            padding: "14px 16px", marginBottom: 16,
          }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#9a3412", marginBottom: 8 }}>
              ⚠ Sicherheitshinweis — ENTWURF
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: "#c2410c", lineHeight: 1.8 }}>
              <li>Ergebnis ist ein <strong>unverbindlicher Entwurf</strong> — keine technische Freigabe.</li>
              <li>Elektrische Endprüfung und Freigabe durch <strong>qualifizierten Elektriker</strong> zwingend erforderlich.</li>
              <li><strong>Keine TOR-Freigabe</strong> (Technische Anschlussbedingungen) und keine Netz-Zustimmung.</li>
              <li>KI darf <strong>nicht freigeben</strong> (canApprove: false — strukturelle Anforderung).</li>
              <li>Voc-Berechnung basiert auf STC-Daten und angenommener Mindesttemperatur — Datenblatt pruefen.</li>
              <li>DC/AC-Verhaeltnis &gt;1.25 Ertragsverluste durch Clipping pruefen (kein Fehler, aber Warnung).</li>
            </ul>
          </div>
        </div>
      )}

      {!r && !running && !error && (
        <div className="card" style={{ textAlign: "center", padding: 32, color: "var(--color-text-muted)", fontSize: 13 }}>
          Eingabedaten aus Modul-Datenblatt und Wechselrichter-Datenblatt eingeben, dann „Stringing prüfen" klicken.
        </div>
      )}
    </div>
  );
}

// ─── Steckbrief ───────────────────────────────────────────────────────────────

interface PresentationSummary {
  project?: { name?: string; address?: string; status?: string };
  customer?: { name?: string };
  // API liefert latestSimulation / latestEconomics / latestEngineering
  latestSimulation?: {
    annualYieldKwh?: number | null;
    specificYieldKwhPerKwp?: number | null;
    performanceRatio?: number | null;
    confidenceScore?: number | null;
  } | null;
  latestEconomics?: {
    paybackYears?: number | null;
    annualSavingsEurExpected?: number | null;
    npvEur?: number | null;
    roi?: number | null;
    co2SavedKgPerYear?: number | null;
  } | null;
  latestEngineering?: {
    modulesTotal?: number | null;
    numberOfStrings?: number | null;
    dcCurrentA?: number | null;
    recommendedCableMm2?: number | null;
  } | null;
  energyScore?: { score?: number; label?: string; explanation?: string } | null;
}

// §17 KI-Prüfung: deterministische Engine-Prüfung UND KI-Einschätzung GETRENNT.
function KiPruefungTab({ projectId }: { projectId: string }) {
  const [summary, setSummary] = useState<PresentationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await apiClient.get<PresentationSummary>(`/projects/${projectId}/presentation`);
      if (!cancelled) { setSummary(res.success ? res.data : null); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const sim = summary?.latestSimulation;
  const eco = summary?.latestEconomics;
  const eng = summary?.latestEngineering;
  const score = summary?.energyScore;
  type Check = { label: string; state: "ok" | "warn" | "grey"; detail: string };
  const checks: Check[] = [
    { label: "PV-Simulation (Ertrag)", state: sim?.specificYieldKwhPerKwp ? "ok" : "grey",
      detail: sim?.specificYieldKwhPerKwp ? `${Math.round(sim.specificYieldKwhPerKwp)} kWh/kWp · Konfidenz ${sim.confidenceScore != null ? Math.round(sim.confidenceScore * 100) + " %" : "—"}` : "Noch keine Simulation." },
    { label: "Wirtschaftlichkeit", state: eco == null ? "grey" : (eco.npvEur != null && eco.npvEur < 0) ? "warn" : "ok",
      detail: eco == null ? "Noch nicht gerechnet." : `NPV ${eco.npvEur != null ? (eco.npvEur >= 0 ? "+" : "") + Math.round(eco.npvEur) + " €" : "—"} · Amortisation ${eco.paybackYears != null ? eco.paybackYears + " J" : "—"}` },
    { label: "Elektroplanung (Stringing)", state: (eng?.numberOfStrings || eng?.modulesTotal) ? "ok" : "grey",
      detail: (eng?.numberOfStrings || eng?.modulesTotal) ? `${eng?.modulesTotal ?? "—"} Module · ${eng?.numberOfStrings ?? "—"} Strings` : "Noch nicht ausgelegt." },
    { label: "Energy Score", state: score?.score != null ? "ok" : "grey",
      detail: score?.score != null ? `${score.score}/100 — ${score.label ?? ""}` : "Noch nicht bewertet." },
  ];
  const dot: Record<string, { c: string; s: string }> = { ok: { c: "#16a34a", s: "✓" }, warn: { c: "#d97706", s: "▲" }, grey: { c: "#94a3b8", s: "○" } };

  return (
    <div>
      <div className="card" style={{ background: "#eff6ff", border: "1px solid #bfdbfe", padding: 12, marginBottom: 16, fontSize: 13 }}>
        <strong>Prüfung getrennt dargestellt (Pflichtenheft §17).</strong> Links die <strong>deterministische</strong> Engine-Prüfung (reine Rechenergebnisse), rechts die <strong>KI-Einschätzung</strong>. Die KI erteilt <strong>keine Freigabe</strong> (canApprove strukturell false) — Freigaben nur durch einen Menschen im Tab „Freigaben".
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignItems: "start" }}>
        <div className="card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Deterministische Prüfung (Engines)</h3>
          {loading ? <div className="muted">Lade…</div> : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
              {checks.map((c) => (
                <li key={c.label} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span aria-hidden="true" style={{ color: dot[c.state]!.c, fontWeight: 700 }}>{dot[c.state]!.s}</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{c.label}</div>
                    <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{c.detail}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <ProjectCopilot projectId={projectId} />
        </div>
      </div>
    </div>
  );
}

function SteckbriefTab({ projectId }: { projectId: string }) {
  const [summary, setSummary] = useState<PresentationSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [angebotLoading, setAngebotLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLoad = async () => {
    setLoading(true);
    setError(null);
    const res = await apiClient.get<PresentationSummary>(`/projects/${projectId}/presentation`);
    if (res.success) {
      setSummary(res.data);
    } else {
      setError("Steckbrief konnte nicht geladen werden.");
    }
    setLoading(false);
  };

  /** Auth-Token aus Zustand-Store lesen */
  function getAuthToken(): string | null {
    const stored = localStorage.getItem("energyos-auth");
    if (!stored) return null;
    try {
      return (
        (JSON.parse(stored) as { state?: { user?: { token?: string } } }).state?.user?.token ?? null
      );
    } catch {
      return null;
    }
  }

  /** Beliebige Auth-geschützte HTML-Route → Blob-URL → neues Tab */
  async function openHtmlReport(apiUrl: string, errMsg: string): Promise<void> {
    const token = getAuthToken();
    const res = await fetch(apiUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const html = await res.text();
    const blob = new Blob([html], { type: "text/html; charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 15_000);
    void errMsg; // suppress unused-var warning
  }

  // Interner Druckbericht
  const handleOpenReport = async () => {
    setReportLoading(true);
    try {
      await openHtmlReport(
        `/api/projects/${projectId}/presentation/report`,
        "Druckbericht konnte nicht geladen werden.",
      );
    } catch {
      setError("Druckbericht konnte nicht geladen werden.");
    }
    setReportLoading(false);
  };

  // Kunden-Angebot (professionell, A4)
  const handleOpenAngebot = async () => {
    setAngebotLoading(true);
    try {
      await openHtmlReport(
        `/api/projects/${projectId}/presentation/angebot`,
        "Kundenangebot konnte nicht geladen werden.",
      );
    } catch {
      setError("Kundenangebot konnte nicht geladen werden.");
    }
    setAngebotLoading(false);
  };

  const sim = summary?.latestSimulation;
  const eco = summary?.latestEconomics;
  const eng = summary?.latestEngineering;
  const score = summary?.energyScore;

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Steckbrief</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-primary" onClick={() => void handleLoad()} disabled={loading}>
            {loading ? "Lade…" : "Steckbrief laden"}
          </button>
          {summary && (
            <button className="btn-secondary" onClick={() => void handleOpenReport()} disabled={reportLoading}>
              {reportLoading ? "Generiere…" : "Druckbericht"}
            </button>
          )}
          <button className="btn-secondary" onClick={() => void handleOpenAngebot()} disabled={angebotLoading}>
            {angebotLoading ? "Generiere…" : "Kundenangebot"}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, color: "#dc2626" }}>
          {error}
        </div>
      )}

      {!summary && !loading && !error && (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>
          Klicken Sie auf „Steckbrief laden", um eine Zusammenfassung aller Berechnungen zu sehen.
        </div>
      )}

      {summary && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* §20-Pflichtregel: Präsentation darf Risiken NICHT verschweigen — Vertrauensfaktor
              (confidenceScore) + Entwurf-Hinweis prominent, statt (wie bisher) nur im Payload. */}
          {(() => {
            const cs = sim?.confidenceScore;
            const pct = typeof cs === "number" ? Math.round(cs * 100) : null;
            const low = pct != null && pct < 70;
            return (
              <div className="card" style={{ padding: 12, background: low ? "#fffbeb" : "#eff6ff", border: `1px solid ${low ? "#fcd34d" : "#bfdbfe"}`, fontSize: 13, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span aria-hidden="true">{low ? "⚠️" : "ℹ️"}</span>
                <strong>Vertrauensfaktor: {pct != null ? `${pct} %` : "—"}</strong>
                <span style={{ color: "var(--color-text-muted)" }}>
                  {pct == null
                    ? "Noch keine Simulation — die Werte sind unverbindliche Richtwerte."
                    : low
                      ? "Niedrig — die Werte sind unsicher und vor einem Angebot zu prüfen."
                      : "Auf Basis der letzten Simulation."}
                  {" "}Alle Angaben sind ein <strong>Entwurf, keine Zusage</strong>; endgültige Werte nach Vor-Ort-Prüfung.
                </span>
              </div>
            );
          })()}
          {/* Energy Score */}
          {score && (
            <div className="card" style={{ textAlign: "center", padding: 24 }}>
              <div style={{ fontSize: 48, fontWeight: 700, color: "var(--color-primary)" }}>{score.score}</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--color-primary)", marginTop: 4 }}>{score.label}</div>
              {score.explanation && (
                <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 8 }}>{score.explanation}</div>
              )}
            </div>
          )}

          {/* Simulation */}
          {sim && (
            <div className="card">
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>☀️ Simulation</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                {sim.annualYieldKwh != null && (
                  <KpiCard label="Jahresertrag" value={`${num(sim.annualYieldKwh)} kWh`} />
                )}
                {sim.specificYieldKwhPerKwp != null && (
                  <KpiCard label="Spez. Ertrag" value={`${num(sim.specificYieldKwhPerKwp)} kWh/kWp`} />
                )}
                {sim.performanceRatio != null && (
                  <KpiCard label="Performance Ratio" value={pct(sim.performanceRatio * 100, 1)} />
                )}
              </div>
            </div>
          )}

          {/* Wirtschaftlichkeit */}
          {eco && (
            <div className="card">
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>💰 Wirtschaftlichkeit</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                {eco.paybackYears != null && (
                  <KpiCard label="Amortisation" value={`${num(eco.paybackYears, 1)} Jahre`} />
                )}
                {eco.annualSavingsEurExpected != null && (
                  <KpiCard label="Jährl. Einsparung" value={`${eur(eco.annualSavingsEurExpected)}/J`} />
                )}
                {eco.npvEur != null && (
                  <KpiCard label="NPV" value={eur(eco.npvEur)} tone={eco.npvEur < 0 ? "negative" : undefined} />
                )}
                {eco.co2SavedKgPerYear != null && (
                  <KpiCard label="CO₂-Einsparung" value={`${num(eco.co2SavedKgPerYear)} kg/J`} />
                )}
              </div>
            </div>
          )}

          {/* Elektroplanung */}
          {eng && (
            <div className="card">
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>⚡ Elektroplanung</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                {eng.modulesTotal != null && (
                  <KpiCard label="Modulanzahl" value={`${eng.modulesTotal} Stk.`} />
                )}
                {eng.numberOfStrings != null && (
                  <KpiCard label="Strings" value={`${eng.numberOfStrings}`} />
                )}
                {eng.dcCurrentA != null && (
                  <KpiCard label="DC-Strom" value={`${num(eng.dcCurrentA, 2)} A`} />
                )}
                {eng.recommendedCableMm2 != null && (
                  <KpiCard label="Kabelquerschnitt" value={`${eng.recommendedCableMm2} mm²`} />
                )}
              </div>
            </div>
          )}

          {!sim && !eco && !eng && !score && (
            <div className="card" style={{ textAlign: "center", padding: 32, color: "var(--color-text-muted)", fontSize: 13 }}>
              Noch keine Berechnungsdaten vorhanden. Führen Sie zuerst eine Simulation, Wirtschaftlichkeitsanalyse und Elektroplanung durch.
            </div>
          )}

        </div>
      )}
    </div>
  );
}

// ─── Freigaben ────────────────────────────────────────────────────────────────

function ApprovalsTab({ projectId, revisionId, approvals }: {
  projectId: string;
  revisionId: string;
  approvals: Array<{ id: string; type: string; status: string; revisionId: string }>;
}) {
  const [localApprovals, setLocalApprovals] = useState(
    approvals.filter(a => a.revisionId === revisionId && a.status !== "superseded")
  );
  const [setting, setSetting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const role = useAuthStore((s) => s.user?.role ?? "");
  const canApprove = (type: string) => (APPROVAL_ALLOWED_ROLES[type] ?? []).includes(role);
  const getStatus = (type: string) => localApprovals.find(a => a.type === type)?.status ?? "pending";
  const handleApprove = async (type: string) => {
    setSetting(type);
    setError(null);
    const res = await apiClient.post<{ id: string; type: string; status: string; revisionId: string }>(
      `/projects/${projectId}/approvals`,
      { revisionId, type, status: "approved" },
    );
    if (res.success) {
      setLocalApprovals(prev => [...prev.filter(a => a.type !== type), { ...res.data, revisionId }]);
    } else {
      // Kein stiller Fehler mehr: 403 (fehlende Rolle) o.Ä. sichtbar machen.
      setError(res.error.message || "Freigabe fehlgeschlagen — fehlt die Berechtigung für diesen Freigabetyp?");
    }
    setSetting(null);
  };
  const STATUS_LABELS: Record<string, string> = {
    approved: "Freigegeben", approved_with_warnings: "Freigegeben mit Hinweis",
    rejected: "Abgelehnt", not_required: "Nicht erforderlich", pending: "Ausstehend",
  };
  const STATUS_COLORS: Record<string, string> = {
    approved: "var(--color-success)", approved_with_warnings: "var(--color-warning)",
    rejected: "var(--color-error)", not_required: "var(--color-text-muted)", pending: "var(--color-text-muted)",
  };
  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Freigaben</h2>
      {error && (
        <div style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 6, background: "#fee2e2", border: "1px solid #fca5a5", color: "#dc2626", fontSize: 13 }}>
          ⚠ {error}
        </div>
      )}
      <div className="card">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ borderBottom: "1px solid var(--color-border)" }}>
            {["Freigabetyp", "Status", "Aktion"].map(h => (
              <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 12, color: "var(--color-text-muted)" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {APPROVAL_TYPES.map(type => {
              const status = getStatus(type);
              const isApproved = status === "approved" || status === "approved_with_warnings";
              return (
                <tr key={type} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "10px 12px", fontWeight: 500 }}>{APPROVAL_TYPE_LABELS[type] ?? type}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ color: STATUS_COLORS[status] ?? "inherit", fontSize: 13 }}>
                      {isApproved ? "✓ " : ""}{STATUS_LABELS[status] ?? status}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px" }}>
                    {!isApproved && revisionId ? (
                      <button className="btn-secondary" style={{ fontSize: 12, padding: "4px 10px" }}
                        onClick={() => void handleApprove(type)}
                        disabled={setting === type || !canApprove(type)}
                        title={!canApprove(type) ? "Ihre Rolle darf diesen Freigabetyp nicht erteilen" : undefined}>
                        {setting === type ? "…" : canApprove(type) ? "Freigeben" : "Keine Berechtigung"}
                      </button>
                    ) : <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>–</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── ServiceHub ───────────────────────────────────────────────────────────────

function ServiceHubTab({ project }: { project: Project }) {
  const currentRevision = project.revisions[0];
  const guard1 = project.status === "ready_for_offer";
  const guard2 = project.revisions.some(r => r.isLocked);
  const guard3 = currentRevision
    ? project.approvals.some(a => a.type === "offer_export" && a.status === "approved" && a.revisionId === currentRevision.id)
    : false;
  const allPassed = guard1 && guard2 && guard3;

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [offerId, setOfferId] = useState<string | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    setOfferId(null);
    const base = `/projects/${project.id}/servicehub-export`;
    const prep = await apiClient.post<{ id: string }>(`${base}/prepare`, {});
    if (!prep.success || !prep.data?.id) {
      setExportError(prep.success ? "Export-Vorbereitung fehlgeschlagen." : prep.error.message);
      setExporting(false);
      return;
    }
    const exportId = prep.data.id;
    const val = await apiClient.post<{ validationResult?: { valid?: boolean; errors?: string[] } }>(
      `${base}/${exportId}/validate`,
      {},
    );
    if (!val.success) {
      setExportError(val.error.message);
      setExporting(false);
      return;
    }
    if (val.data?.validationResult && val.data.validationResult.valid === false) {
      setExportError(`Validierung fehlgeschlagen: ${val.data.validationResult.errors?.join(", ") ?? "unbekannt"}`);
      setExporting(false);
      return;
    }
    const exec = await apiClient.post<{ result?: { success?: boolean; serviceHubOfferId?: string | null; errorMessage?: string } }>(
      `${base}/${exportId}/execute`,
      {},
    );
    if (!exec.success) {
      setExportError(exec.error.message);
    } else if (exec.data?.result?.success && exec.data.result.serviceHubOfferId) {
      setOfferId(exec.data.result.serviceHubOfferId);
    } else {
      setExportError(exec.data?.result?.errorMessage ?? "Export fehlgeschlagen.");
    }
    setExporting(false);
  };

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>ServiceHub-Export</h2>
      <div className="card">
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Voraussetzungen für Export:</div>
          {([["guard1", guard1, "Projekt-Status = ready_for_offer"], ["guard2", guard2, "Mindestens eine gesperrte Revision"], ["guard3", guard3, "Freigabe offer_export = approved"]] as [string, boolean, string][]).map(([key, ok, req]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 6 }}>
              <span style={{ color: ok ? "var(--color-success)" : "var(--color-error)", fontWeight: 700 }}>{ok ? "✓" : "✗"}</span>
              <span style={{ color: ok ? "var(--color-text)" : "var(--color-text-muted)" }}>{req}</span>
            </div>
          ))}
        </div>
        <button className="btn-secondary" disabled={!allPassed || exporting} onClick={() => void handleExport()}>
          {exporting
            ? "Export läuft…"
            : allPassed
              ? "Export vorbereiten & senden"
              : "Export vorbereiten (Voraussetzungen nicht erfüllt)"}
        </button>

        {offerId && (
          offerId.startsWith("MOCK-") ? (
            <div style={{ marginTop: 12, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, padding: 12, fontSize: 13, color: "#92400e" }}>
              <span style={{ display: "inline-block", background: "#d97706", color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 10, marginRight: 8, letterSpacing: "0.04em" }}>MOCK · SIMULIERT</span>
              <strong>Kein realer ServiceHub-Export.</strong> Der Transport-Connector ist noch nicht angebunden — die Angebots-ID <strong>{offerId}</strong> ist simuliert. Guards (Status/Revision/Freigabe), Idempotenz und Audit-Log liefen echt.
            </div>
          ) : (
            <div style={{ marginTop: 12, background: "var(--color-success-tint, #f0fdf4)", border: "1px solid var(--color-success, #86efac)", borderRadius: 6, padding: 12, fontSize: 13 }}>
              ✓ Export erfolgreich. ServiceHub-Angebots-ID: <strong>{offerId}</strong>
            </div>
          )
        )}
        {exportError && (
          <div style={{ marginTop: 12, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, fontSize: 13, color: "#dc2626" }}>
            {exportError}
          </div>
        )}

        <div style={{ marginTop: 16, fontSize: 12, color: "var(--color-text-muted)", lineHeight: 1.5 }}>
          Export-Workflow, Dreifach-Guard (Status, gesperrte Revision, Freigabe), Idempotenz und Audit-Log
          sind vollständig implementiert. Der externe Transport an ServiceHub ist bis zur Anbindung des
          echten Connectors simuliert (Mock-Angebots-ID).
        </div>
      </div>
    </div>
  );
}

// ─── Audit ────────────────────────────────────────────────────────────────────

function AuditTab({ projectId }: { projectId: string }) {
  const [logs, setLogs] = useState<Array<{ id: string; action: string; resourceType: string; createdAt: string }>>([]);
  useEffect(() => {
    void (async () => {
      const res = await apiClient.get<{ items: typeof logs }>(`/audit?resourceId=${projectId}`);
      if (res.success) setLogs(res.data.items ?? []);
    })();
  }, [projectId]);
  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Audit-Log</h2>
      <div className="card">
        {logs.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: "var(--color-text-muted)" }}>Keine Einträge für dieses Projekt.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                {["Zeitpunkt", "Aktion", "Ressourcentyp"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "6px 12px", fontSize: 12, color: "var(--color-text-muted)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                  <td style={{ padding: "8px 12px", fontSize: 12, color: "var(--color-text-muted)" }}>{new Date(log.createdAt).toLocaleString("de-AT")}</td>
                  <td style={{ padding: "8px 12px", fontSize: 13, fontFamily: "monospace" }}>{log.action}</td>
                  <td style={{ padding: "8px 12px", fontSize: 13 }}>{log.resourceType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Komponenten-Tab (Wechselrichter + Batterie Empfehlung) ───────────────────

interface WechselrichterFe {
  id: string;
  hersteller: string;
  modell: string;
  leistungKw: number;
  mppTrackers: number;
  wirkungsgrad: number;
  preisEur: number;
  norm?: string;
  torTyp?: string;
}

interface BatterieFe {
  id: string;
  hersteller: string;
  modell: string;
  kapazitaetKwh: number;
  nutzbarKwh: number;
  zyklen: number;
  preisEur: number;
}

interface EmpfehlungResult {
  wechselrichter: WechselrichterFe;
  alternativWr: WechselrichterFe | null;
  batterie: BatterieFe | null;
  batterieGroesseKwh: number;
  stringKonfiguration: {
    mppTracker: number;
    modulePro: number[];
    stringLaenge: number;
    parallelStrings: number;
  };
  gesamtpreisNetto: number;
  gesamtpreisBrutto: number;
  anmerkungen: string[];
}

// PV-Modul aus dem Komponenten-Katalog (für die pvXpert-artige Auslegung).
interface KomponentTabModule {
  id: string;
  manufacturer: string;
  modelName: string;
  specs?: { nominalPowerWp?: number; vocV?: number } | null;
}

function KomponentenTab({ projectId }: { projectId: string }) {
  const [compKwp, setCompKwp] = useState<number>(0);
  const [compVerbrauch, setCompVerbrauch] = useState<number>(4000);
  const [compBatterie, setCompBatterie] = useState<boolean>(false);
  const [compDachflaechen, setCompDachflaechen] = useState<number>(1);
  const [compEmpfehlung, setCompEmpfehlung] = useState<EmpfehlungResult | null>(null);
  const [compLoading, setCompLoading] = useState(false);
  const [compError, setCompError] = useState<string | null>(null);
  // Modul-Katalog + Auswahl (reale Specs → präzise String-Auslegung)
  const [modules, setModules] = useState<KomponentTabModule[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<string>("");
  // Herkunft des kWp-Werts (Dachentwurf vs. manuell)
  const [kwpSource, setKwpSource] = useState<string | null>(null);
  // Stückliste/Montageplan-Druckblatt
  const [showStueckliste, setShowStueckliste] = useState(false);

  const selectedModule = modules.find((m) => m.id === selectedModuleId) ?? null;
  // Voc des gewählten Moduls (für String-Spannungs-Anzeige); Default 50 V.
  const moduleVoc =
    typeof selectedModule?.specs?.vocV === "number" ? selectedModule.specs.vocV : 50;

  // PV-Module aus dem Katalog laden (einmalig).
  useEffect(() => {
    void apiClient.get<KomponentTabModule[]>("/components?type=pv_module").then((r) => {
      if (r.success) setModules(Array.isArray(r.data) ? r.data : []);
    });
  }, []);

  // kWp automatisch aus dem neuesten Dachentwurf übernehmen (pvXpert-Flow:
  // Dach zeichnen → Systemgröße steht). Nur als Startwert, manuell überschreibbar.
  useEffect(() => {
    void apiClient
      .get<Array<{ summaryJson?: { result?: { estimatedKwp?: number; moduleCount?: number } } }>>(
        `/projects/${projectId}/roof-designs`,
      )
      .then((r) => {
        if (!r.success || !Array.isArray(r.data) || r.data.length === 0) return;
        const kwp = r.data[0]?.summaryJson?.result?.estimatedKwp;
        if (typeof kwp === "number" && kwp > 0) {
          setCompKwp(Math.round(kwp * 100) / 100);
          setKwpSource("Dachentwurf");
        }
      });
  }, [projectId]);

  async function fetchEmpfehlung() {
    setCompLoading(true);
    setCompError(null);
    try {
      const params = new URLSearchParams({
        kwp: compKwp.toString(),
        verbrauch: compVerbrauch.toString(),
        batterie: compBatterie.toString(),
        dachflaechen: compDachflaechen.toString(),
      });
      // Reale Modul-Specs anhängen → Engine nutzt sie statt 420-Wp-Default.
      const modWp = selectedModule?.specs?.nominalPowerWp;
      if (typeof modWp === "number" && modWp > 0) params.set("modulWp", String(modWp));
      if (typeof selectedModule?.specs?.vocV === "number") params.set("modulVoc", String(selectedModule.specs.vocV));
      const res = await apiClient.get<EmpfehlungResult>(
        `/components/empfehlung?${params.toString()}`,
      );
      if (!res.success) throw new Error("Empfehlung konnte nicht berechnet werden.");
      setCompEmpfehlung(res.data);
    } catch (e) {
      setCompError(e instanceof Error ? e.message : "Unbekannter Fehler");
    } finally {
      setCompLoading(false);
    }
  }

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Wechselrichter & Batterie Empfehlung</h2>

      {/* Eingabe-Formular */}
      <div className="card" style={{ marginBottom: 16, padding: 20 }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600 }}>Systemauslegung</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <span style={{ fontSize: 12, color: "#374151" }}>
              PV-Leistung (kWp)
              {kwpSource && (
                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: "#15803d", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 4, padding: "1px 6px" }}>
                  aus {kwpSource}
                </span>
              )}
            </span>
            <input
              type="number" min={1} max={100} step={0.5} value={compKwp || ""}
              onChange={e => { setCompKwp(parseFloat(e.target.value) || 0); setKwpSource(null); }}
              style={{ display: "block", width: "100%", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, boxSizing: "border-box", marginTop: 4 }}
            />
          </label>
          <label>
            <span style={{ fontSize: 12, color: "#374151" }}>PV-Modul (Katalog)</span>
            <select
              value={selectedModuleId}
              onChange={e => setSelectedModuleId(e.target.value)}
              style={{ display: "block", width: "100%", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, boxSizing: "border-box", marginTop: 4 }}
            >
              <option value="">— Standard-Annahme (420 Wp) —</option>
              {modules.filter(m => m.specs?.nominalPowerWp).map(m => (
                <option key={m.id} value={m.id}>
                  {m.manufacturer} {m.modelName} ({m.specs!.nominalPowerWp} Wp)
                </option>
              ))}
            </select>
          </label>
          <label>
            <span style={{ fontSize: 12, color: "#374151" }}>Jahresverbrauch (kWh)</span>
            <input
              type="number" min={500} max={100000} step={100} value={compVerbrauch}
              onChange={e => setCompVerbrauch(parseFloat(e.target.value) || 4000)}
              style={{ display: "block", width: "100%", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, boxSizing: "border-box", marginTop: 4 }}
            />
          </label>
          <label>
            <span style={{ fontSize: 12, color: "#374151" }}>Dachausrichtungen</span>
            <select
              value={compDachflaechen}
              onChange={e => setCompDachflaechen(parseInt(e.target.value, 10))}
              style={{ display: "block", width: "100%", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, boxSizing: "border-box", marginTop: 4 }}
            >
              <option value={1}>1 (Einfach)</option>
              <option value={2}>2 (Ost/West oder Süd+Seite)</option>
              <option value={3}>3+ (Komplex)</option>
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 20 }}>
            <input
              type="checkbox" checked={compBatterie}
              onChange={e => setCompBatterie(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontSize: 14, color: "#374151" }}>Batteriespeicher gewünscht</span>
          </label>
        </div>
        <button
          onClick={() => { void fetchEmpfehlung(); }}
          disabled={compLoading || compKwp <= 0}
          style={{ marginTop: 16, padding: "10px 24px", background: compKwp > 0 ? "#2563eb" : "#9ca3af", color: "#fff", border: "none", borderRadius: 6, cursor: compKwp > 0 ? "pointer" : "not-allowed", fontSize: 14, fontWeight: 600 }}
        >
          {compLoading ? "Berechnung läuft..." : "Empfehlung berechnen"}
        </button>
        {compError && <p style={{ color: "#dc2626", marginTop: 8, fontSize: 13 }}>{compError}</p>}
      </div>

      {/* Ergebnis */}
      {compEmpfehlung && (
        <div>
          {/* Stückliste / Montageplan exportieren */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button
              onClick={() => setShowStueckliste(true)}
              style={{ padding: "8px 16px", background: "#0f766e", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
            >
              📋 Stückliste & Montageplan
            </button>
          </div>
          {/* Wechselrichter */}
          <div className="card" style={{ marginBottom: 12, padding: 20 }}>
            <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600, color: "#1d4ed8" }}>
              Empfohlener Wechselrichter
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <div style={{ background: "#f0f9ff", borderRadius: 8, padding: 12 }}>
                <p style={{ margin: 0, fontSize: 11, color: "#0369a1" }}>Hersteller &amp; Modell</p>
                <p style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 700, color: "#0c4a6e" }}>
                  {compEmpfehlung.wechselrichter.hersteller}
                </p>
                <p style={{ margin: "2px 0 0", fontSize: 13, color: "#0369a1" }}>
                  {compEmpfehlung.wechselrichter.modell}
                </p>
              </div>
              <div style={{ background: "#f0f9ff", borderRadius: 8, padding: 12 }}>
                <p style={{ margin: 0, fontSize: 11, color: "#0369a1" }}>Leistung</p>
                <p style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 700, color: "#0c4a6e" }}>
                  {compEmpfehlung.wechselrichter.leistungKw} kW
                </p>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#0369a1" }}>
                  {"η"} = {compEmpfehlung.wechselrichter.wirkungsgrad}%
                </p>
              </div>
              <div style={{ background: "#f0f9ff", borderRadius: 8, padding: 12 }}>
                <p style={{ margin: 0, fontSize: 11, color: "#0369a1" }}>Preis (netto)</p>
                <p style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 700, color: "#0c4a6e" }}>
                  {compEmpfehlung.wechselrichter.preisEur.toLocaleString("de-AT")} {"€"}
                </p>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#0369a1" }}>
                  {compEmpfehlung.wechselrichter.mppTrackers} MPP-Tracker
                </p>
              </div>
            </div>
            {compEmpfehlung.alternativWr && (
              <div style={{ marginTop: 10, padding: 10, background: "#fafafa", borderRadius: 6, border: "1px solid #e5e7eb" }}>
                <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                  Alternative: <strong>{compEmpfehlung.alternativWr.hersteller} {compEmpfehlung.alternativWr.modell}</strong>
                  {" — "}{compEmpfehlung.alternativWr.leistungKw} kW, {compEmpfehlung.alternativWr.preisEur.toLocaleString("de-AT")} {"€"} netto
                </p>
              </div>
            )}
            {/* Netzkonformität (TOR) */}
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#166534", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 4, padding: "2px 8px" }}>
                ✓ Netzkonform: {compEmpfehlung.wechselrichter.norm ?? "EN 50549-1"} · TOR Erzeuger Typ {compEmpfehlung.wechselrichter.torTyp ?? "A"}
              </span>
              <span style={{ fontSize: 11, color: "#6b7280" }}>
                Verbindliche Netzzulassung:{" "}
                <a href="https://www.wechselrichterliste.at" target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>
                  wechselrichterliste.at
                </a>
              </span>
            </div>
          </div>

          {/* String-Konfiguration */}
          <div className="card" style={{ marginBottom: 12, padding: 20 }}>
            <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600, color: "#065f46" }}>
              String-Konfiguration
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              <div style={{ background: "#ecfdf5", borderRadius: 8, padding: 12 }}>
                <p style={{ margin: 0, fontSize: 11, color: "#047857" }}>MPP-Tracker</p>
                <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 700, color: "#064e3b" }}>
                  {compEmpfehlung.stringKonfiguration.mppTracker}
                </p>
              </div>
              <div style={{ background: "#ecfdf5", borderRadius: 8, padding: 12 }}>
                <p style={{ margin: 0, fontSize: 11, color: "#047857" }}>Module in Reihe</p>
                <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 700, color: "#064e3b" }}>
                  {compEmpfehlung.stringKonfiguration.stringLaenge}
                </p>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#047857" }}>
                  {"≈"} {(compEmpfehlung.stringKonfiguration.stringLaenge * moduleVoc).toFixed(0)} V (Voc)
                </p>
              </div>
              <div style={{ background: "#ecfdf5", borderRadius: 8, padding: 12 }}>
                <p style={{ margin: 0, fontSize: 11, color: "#047857" }}>Module pro Tracker</p>
                <p style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 700, color: "#064e3b" }}>
                  {compEmpfehlung.stringKonfiguration.modulePro.join(" / ")}
                </p>
              </div>
            </div>
          </div>

          {/* Batterie (conditional) */}
          {compEmpfehlung.batterie && (
            <div className="card" style={{ marginBottom: 12, padding: 20 }}>
              <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600, color: "#7c3aed" }}>
                Empfohlener Batteriespeicher
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div style={{ background: "#f5f3ff", borderRadius: 8, padding: 12 }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#7c3aed" }}>Hersteller &amp; Modell</p>
                  <p style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 700, color: "#4c1d95" }}>
                    {compEmpfehlung.batterie.hersteller}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: "#7c3aed" }}>
                    {compEmpfehlung.batterie.modell}
                  </p>
                </div>
                <div style={{ background: "#f5f3ff", borderRadius: 8, padding: 12 }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#7c3aed" }}>Nutzbare Kapazität</p>
                  <p style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 700, color: "#4c1d95" }}>
                    {compEmpfehlung.batterieGroesseKwh} kWh
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "#7c3aed" }}>
                    {compEmpfehlung.batterie.zyklen.toLocaleString()} Zyklen Garantie
                  </p>
                </div>
                <div style={{ background: "#f5f3ff", borderRadius: 8, padding: 12 }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#7c3aed" }}>Preis (netto)</p>
                  <p style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 700, color: "#4c1d95" }}>
                    {compEmpfehlung.batterie.preisEur.toLocaleString("de-AT")} {"€"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Gesamtpreis */}
          <div className="card" style={{ marginBottom: 12, padding: 20, background: "#1e3a5f", color: "#fff" }}>
            <h4 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 600, color: "#93c5fd" }}>
              Komponentenkosten (ohne Module + Montage)
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <p style={{ margin: 0, fontSize: 12, color: "#93c5fd" }}>Netto</p>
                <p style={{ margin: "4px 0 0", fontSize: 24, fontWeight: 700 }}>
                  {compEmpfehlung.gesamtpreisNetto.toLocaleString("de-AT")} {"€"}
                </p>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 12, color: "#93c5fd" }}>Brutto (inkl. 20% MwSt.)</p>
                <p style={{ margin: "4px 0 0", fontSize: 24, fontWeight: 700 }}>
                  {compEmpfehlung.gesamtpreisBrutto.toLocaleString("de-AT")} {"€"}
                </p>
              </div>
            </div>
          </div>

          {/* Anmerkungen */}
          {compEmpfehlung.anmerkungen.length > 0 && (
            <div className="card" style={{ padding: 16 }}>
              {compEmpfehlung.anmerkungen.map((a, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : "6px 0 0", fontSize: 13, color: a.startsWith("Kein") || a.startsWith("Keine") || a.startsWith("String-Spannung") ? "#dc2626" : "#374151" }}>
                  {a}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stückliste / Montageplan — Druckblatt */}
      {showStueckliste && compEmpfehlung && (
        <StuecklistePrintSheet
          kwp={compKwp}
          modulName={selectedModule ? `${selectedModule.manufacturer} ${selectedModule.modelName}` : null}
          modulWp={selectedModule?.specs?.nominalPowerWp ?? 420}
          modulVoc={moduleVoc}
          moduleCount={Math.round((compKwp * 1000) / (selectedModule?.specs?.nominalPowerWp ?? 420))}
          wechselrichter={{
            hersteller: compEmpfehlung.wechselrichter.hersteller,
            modell: compEmpfehlung.wechselrichter.modell,
            leistungKw: compEmpfehlung.wechselrichter.leistungKw,
            preisEur: compEmpfehlung.wechselrichter.preisEur,
            mppTrackers: compEmpfehlung.wechselrichter.mppTrackers,
          }}
          batterie={compEmpfehlung.batterie ? {
            hersteller: compEmpfehlung.batterie.hersteller,
            modell: compEmpfehlung.batterie.modell,
            preisEur: compEmpfehlung.batterie.preisEur,
          } : null}
          batterieKwh={compEmpfehlung.batterieGroesseKwh}
          stringKonfiguration={{
            mppTracker: compEmpfehlung.stringKonfiguration.mppTracker,
            stringLaenge: compEmpfehlung.stringKonfiguration.stringLaenge,
            modulePro: compEmpfehlung.stringKonfiguration.modulePro,
          }}
          gesamtpreisNetto={compEmpfehlung.gesamtpreisNetto}
          gesamtpreisBrutto={compEmpfehlung.gesamtpreisBrutto}
          onClose={() => setShowStueckliste(false)}
        />
      )}

      <TorLookup />
    </div>
  );
}

interface TorGeraetFe {
  marke: string;
  type: string;
  leistungKw: number | null;
  phasen: number | null;
  art: string | null;
  zulaessig: string | null;
  mitEinschraenkung: boolean;
  befristetBis: string | null;
}
interface TorSearchFe {
  stand: string;
  quelleUrl: string;
  count: number;
  matched: number;
  geraete: TorGeraetFe[];
}

// Suche in der offiziellen TOR-Wechselrichterliste (Typ A) — Netzzulassung prüfen
function TorLookup() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState<TorSearchFe | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!q.trim()) {
      setRes(null);
      return;
    }
    setLoading(true);
    try {
      const r = await apiClient.get<TorSearchFe>(
        `/components/tor-liste?q=${encodeURIComponent(q)}&limit=30`,
      );
      setRes(r.success ? r.data : null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 16, padding: 20 }}>
      <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: "#1d4ed8" }}>
        🔌 Netzzulassung prüfen — offizielle TOR-Liste (Typ A)
      </h4>
      <p style={{ margin: "0 0 12px", fontSize: 11, color: "#6b7280" }}>
        Verbindliche Geräteliste aller österreichischen Netzbetreiber (EN 50549-1).{" "}
        <a href="https://www.wechselrichterliste.at" target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>
          wechselrichterliste.at
        </a>
        {res ? ` · Stand ${res.stand} · ${res.count.toLocaleString("de-AT")} Geräte` : ""}
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void run();
          }}
          placeholder="Hersteller oder Typ, z.B. Fronius GEN24"
          style={{ flex: 1, padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14 }}
        />
        <button
          onClick={() => void run()}
          disabled={loading || !q.trim()}
          className="btn-primary"
          style={{ fontSize: 13 }}
        >
          {loading ? "Suche…" : "Suchen"}
        </button>
      </div>
      {res && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 12, color: "#374151", margin: "0 0 8px" }}>
            {res.matched === 0
              ? "Keine zugelassenen Geräte gefunden."
              : `${res.matched} Treffer${res.matched > res.geraete.length ? ` (zeige ${res.geraete.length})` : ""}`}
          </p>
          {res.geraete.length > 0 && (
            <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 6 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {["Marke", "Type", "kW", "Ph", "Zulässig"].map((h) => (
                      <th
                        key={h}
                        style={{ textAlign: "left", padding: "6px 8px", color: "#6b7280", fontWeight: 600, position: "sticky", top: 0, background: "#f8fafc" }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {res.geraete.map((g, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #eef2f7" }}>
                      <td style={{ padding: "5px 8px", fontWeight: 500 }}>{g.marke}</td>
                      <td style={{ padding: "5px 8px" }}>{g.type}</td>
                      <td style={{ padding: "5px 8px" }}>{g.leistungKw ?? "–"}</td>
                      <td style={{ padding: "5px 8px" }}>{g.phasen ?? "–"}</td>
                      <td style={{ padding: "5px 8px" }}>
                        <span
                          style={{ fontSize: 11, padding: "1px 6px", borderRadius: 3, background: g.mitEinschraenkung ? "#fef3c7" : "#dcfce7", color: g.mitEinschraenkung ? "#92400e" : "#166534" }}
                        >
                          {g.zulaessig ?? "–"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Placeholder ──────────────────────────────────────────────────────────────

// Defensiver Fallback für einen unbekannten Tab-Key. Im Normalbetrieb
// unerreichbar (alle Tab-Keys haben eigene Cases) — daher neutraler Text
// statt einer veralteten „Phase 1"-Behauptung.
function PlaceholderTab({ name }: { name: string }) {
  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{name}</h2>
      <div className="card" style={{ marginTop: 16, textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>
        <div>Dieser Bereich ist nicht verfügbar.</div>
      </div>
    </div>
  );
}

// ─── Shared Helpers ───────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "negative" | undefined;
}) {
  const valueColor = tone === "negative" ? "var(--color-error)" : "var(--color-primary)";
  return (
    <div className="card" style={{ textAlign: "center" }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: valueColor, lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4, fontWeight: 500 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "6px 10px",
  border: "1px solid var(--color-border)",
  borderRadius: 4,
  fontSize: 13,
  background: "var(--color-bg)",
  color: "var(--color-text)",
  boxSizing: "border-box",
};
